/**
 * useChartMindSession - Master hook for ChartMind session state management
 * 
 * Composes all ChartMind hooks and lifts component state into a single
 * coordinated session that can be serialized for Firestore persistence.
 * 
 * Phase 1: Hook composition and state consolidation
 * Phase 2: Serialization layer (serializeSession, hydrateSession)
 * Phase 3: Firestore integration (saveSession, loadSession)
 */

import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useDebounce, useDebouncedCallback } from "use-debounce";
import { formatISO } from "date-fns";
// Server-side transcription (OpenAI under our BAA) rather than the browser's Web Speech API.
// Swapped together with the phone page on purpose: two recording surfaces on two different
// engines would mean two accuracy profiles and two sets of browser rules to explain.
// useVoiceRecording is still in the tree, so this is a one-line revert.
import useEncounterRecording from "hooks/useEncounterRecording";
import usePhoneRecordingRelay from "hooks/usePhoneRecordingRelay";
import useChartMind from "hooks/useChartMind";
import useClarifyingQuestions from "hooks/useClarifyingQuestions";
import useChartGeneration from "hooks/useChartGeneration";
import useDiagnosticPlan from "hooks/useDiagnosticPlan";
import useConfirmDiagnosis from "hooks/useConfirmDiagnosis";
import useTreatmentPlan from "hooks/useTreatmentPlan";
import useChartMindDischarge from "hooks/useChartMindDischarge";
import { useAuth } from "hooks/useAuth";
import { STEPS } from "components/dashboard/chartmind/ChartMindHeader";
import * as sessionService from "services/chartMindSessionService";
import {
  downloadBlob,
  exportClinicalNotePdf,
} from "services/documentsService";
import { compactSourceList } from "utils/clinicalSources";
import {
  buildPatientContextPayload,
  patientContextPayloadKey,
} from "utils/patientContext";
import { djb2 } from "utils/hash";
import {
  PEARLS_PROMPT_ID,
  generatePearls,
  isMissingPromptError,
} from "components/dashboard/chartmind/pearls";
import { isSameCondition } from "utils/conditionMatch";
import {
  createBaseExam2Systems,
  defaultGeneralFinding,
  extractPatientSexKey,
  getPreferredExam2Normal,
  mergeExam2Systems,
  normalizeExam2Values,
} from "components/dashboard/chartmind/exam2Catalog";
import {
  loadExam2PreferredNormals,
  setExam2PreferredNormal,
} from "components/dashboard/chartmind/exam2Defaults";
import {
  applyExam2LayoutPrefs,
  loadExam2LayoutPrefs,
  preferredNormalsFromLayoutPrefs,
  saveExam2LayoutPrefs,
  systemsToExam2LayoutPrefs,
} from "components/dashboard/chartmind/exam2LayoutPrefs";
import {
  applyPlan2FocusFlip,
  createEmptyPlan2ByCategory,
  createEmptyTreatment2ByCategory,
  ensurePlan2Defaults,
  fillAllPlan2Ordered,
  fillPlan2Normals,
  fillPlan2ReassuringFromPending,
  hasPlan2State,
  markPlan2PendingFlip,
  normalizePlan2ByCategory,
  normalizePlan2Values,
  normalizeTreatment2ByCategory,
  usesMarkAllReassuring,
} from "components/dashboard/chartmind/plan2Catalog";
import {
  EVENTS_NOTE_PROMPT_ID,
  EVENTS_PLAN_FOCUS,
  INPATIENT_FALLBACK_INSTRUCTIONS,
  normalizeNoteFormat,
  buildEventsNoteContext,
  buildEventsObjectiveText,
  buildEventsSourceText,
  buildExamDiagnosticsNoteContext,
  buildInpatientNoteContext,
  createInpatientEvent,
  ensurePendingInNote,
  isEventsNoteFormat,
  isInpatient,
  normalizeEncounterSetting,
  normalizeInpatientEvents,
  pendingDailyPlanNames,
} from "components/dashboard/chartmind/inpatient";
import {
  eventsDiagnosisPromptOptions,
  eventsPearlsPromptOptions,
  formatDocumentationOutline,
  normalizeSpecialty,
  noteTemplateFor,
  promptOptions,
  specialtyFocus,
} from "components/dashboard/chartmind/specialties";

// Imported patient records are redeemed under a 60-minute, section-scoped consent. Persisting
// the clinical blob in the session document outlives that consent window, so it stays off
// until the client signs off on it. The serialize and hydrate paths below are complete: flip
// this one constant to true and the record survives reloads with no other change.
const PERSIST_IMPORTED_PATIENT_RECORD = false;

// The clinician's last inpatient/outpatient choice, used as the default for new encounters.
// localStorage like the other `chartmind:` prefs; nothing here is PHI.
const ENCOUNTER_SETTING_KEY = "chartmind:encounter-setting";
const NOTE_FORMAT_KEY = "chartmind:note-format";
const SPECIALTY_KEY = "chartmind:specialty";
const readDefaultSpecialty = () => {
  try {
    return normalizeSpecialty(window.localStorage.getItem(SPECIALTY_KEY));
  } catch {
    return normalizeSpecialty(null);
  }
};
const readDefaultNoteFormat = () => {
  try {
    return normalizeNoteFormat(window.localStorage.getItem(NOTE_FORMAT_KEY));
  } catch {
    return normalizeNoteFormat(null);
  }
};
const readDefaultEncounterSetting = () => {
  try {
    return normalizeEncounterSetting(window.localStorage.getItem(ENCOUNTER_SETTING_KEY));
  } catch {
    return normalizeEncounterSetting(null);
  }
};

/**
 * useChartMindSession Hook
 * 
 * @param {string|null} initialSessionId - Optional session ID to load on mount
 * @param {Object} [options]
 * @param {boolean} [options.disableSave=false] - [BETA-KIJABE] When true, all Firestore
 *   persistence is nooped (no auto-save, no manual save, no session ID creation).
 *   Used for Kijabe beta testers so encounters aren't persisted. Remove when beta ends.
 * @returns {Object} Grouped session state and actions
 */
function useChartMindSession(initialSessionId = null, { disableSave = false } = {}) {
  // Get current user for session persistence
  const { user, organizationId, isGlobalAdmin } = useAuth();

  // ============================================================================
  // COMPOSE EXISTING HOOKS
  // ============================================================================
  
  // Note: onDataChanged callbacks will be defined after autoSaveSession is created
  // We'll use a ref to avoid circular dependencies
  const onDataChangedRef = useRef(null);

  // Stable callback that delegates to the ref — must NOT be recreated on every render.
  // Each inline `() => onDataChangedRef.current?.()` would be a new function reference,
  // causing every useCallback inside the sub-hooks (e.g. analyzeDDX) to also change,
  // which makes the auto-analysis effect deps change every render → infinite LLM loop.
  const stableOnDataChanged = useCallback(() => onDataChangedRef.current?.(), []);
  
  const [recordingLanguage, setRecordingLanguageState] = useState(() => {
    try {
      return window.localStorage.getItem("chartmind:recording-language") || "en-US";
    } catch {
      return "en-US";
    }
  });
  const setRecordingLanguage = useCallback((lang) => {
    setRecordingLanguageState(lang);
    try {
      window.localStorage.setItem("chartmind:recording-language", lang);
    } catch {
      // ignore
    }
  }, []);

  // Inpatient or outpatient. The last choice is the default for the next encounter; a saved
  // session restores its own setting without changing that default.
  const [encounterSetting, setEncounterSettingState] = useState(readDefaultEncounterSetting);
  const setEncounterSetting = useCallback((value) => {
    const setting = normalizeEncounterSetting(value);
    const wasInpatient = isInpatient(encounterSetting);
    const nowInpatient = isInpatient(setting);
    setEncounterSettingState(setting);
    try {
      window.localStorage.setItem(ENCOUNTER_SETTING_KEY, setting);
    } catch {
      // ignore
    }
    // Inpatient note types lean *2; outpatient keeps the legacy list/Exam default.
    if (nowInpatient !== wasInpatient) {
      setExamMode(nowInpatient ? "exam2" : "exam");
      setPlan2ByCategory(createEmptyPlan2ByCategory(nowInpatient ? "table" : "list"));
      setTreatment2ByCategory(
        createEmptyTreatment2ByCategory(nowInpatient ? "table" : "list"),
      );
    }
  }, [encounterSetting]);
  const inpatient = isInpatient(encounterSetting);

  // Specialty (ChartMind-7's six), remembered the same way. It picks the note outline and the
  // focus text every step's request carries.
  const [specialty, setSpecialtyState] = useState(readDefaultSpecialty);
  const setSpecialty = useCallback((value) => {
    const next = normalizeSpecialty(value);
    setSpecialtyState(next);
    try {
      window.localStorage.setItem(SPECIALTY_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  // Inpatient note type (admission H&P, progress note, or Events), remembered the same way.
  const [noteFormat, setNoteFormatState] = useState(readDefaultNoteFormat);
  const eventsNote = inpatient && isEventsNoteFormat(noteFormat);
  const recordingHook = useEncounterRecording({ language: recordingLanguage });

  // Typing or pasting instead of recording. Held here, not on the Record step, so it is still
  // in effect when the clinician comes back to that step; a new encounter starts in voice again.
  const [textMode, setTextMode] = useState(false);

  // Clarifying questions are always on: they cost nothing on screen until there is one to ask,
  // so there is no switch for them.
  const clarifyingQuestionsEnabled = true;

  // Patient context imported from a patient-generated share code (QR / 7-digit).
  // Lives at session-level so the auto-analysis effect can re-fire when the
  // clinician scans a code mid-encounter, and so downstream steps can read it.
  // Declared before the step hooks because every one of them now consumes it.
  const [patientContext, setPatientContext] = useState(null);
  const clearPatientContext = useCallback(() => setPatientContext(null), []);

  // The single rendered form of the imported record that every step sends to the LLM.
  //
  // This is the fix for the ChartMind demo failure: previously only the differential
  // formatted the record, so medications and allergies never reached the diagnostic plan,
  // confirm, treatment, discharge or note prompts. Built once here and threaded into every
  // generator below, so a new step cannot quietly opt out of it.
  const patientContextPayload = useMemo(
    () => buildPatientContextPayload(patientContext),
    [patientContext],
  );

  // A session restored from Firestore keeps the patient's identity but not the clinical
  // record (see PERSIST_IMPORTED_PATIENT_RECORD). Surface that rather than letting every
  // step silently run record-blind, which is the original bug wearing a different hat.
  const patientRecordUnavailable = Boolean(
    patientContext?.patientUserId && !patientContext?.record,
  );

  // ============================================================================
  // PHONE MICROPHONE RELAY
  // ============================================================================
  //
  // Clinic desk mics are bad, so the clinician can record on their phone instead and have the
  // transcript stream in here. Only the transcript crosses; see CHARTMIND-PHONE-MIC.md.
  //
  // Mounted here rather than in RecordingStep so the pairing survives navigating away from the
  // Record step, exactly like the rest of this session.
  //
  // onTranscript is recordingHook.updateTranscript directly: the relay is just another writer
  // of the one string ChartMind already reads everywhere.
  //
  // onFinished has to reach finishCapture, which is defined further down because it depends on
  // runDdx. Rather than reorder half this file, it goes through a ref behind a stable callback —
  // the same shape as stableOnDataChanged above, and for the same reason: an inline arrow here
  // would be a new identity every render and would re-subscribe the relay on every keystroke.
  const finishCaptureRef = useRef(null);

  // ============================================================================
  // PHONE DICTATION INTO A CHART SECTION
  // ============================================================================
  //
  // Same wire, different destination. The clinician opens a note section's editor on the Chart
  // step, presses "Use phone", and the round they speak lands in that section instead of in the
  // encounter. See CHARTMIND-PHONE-DICTATION.md.
  //
  // ⚠️ THE TARGET IS ARMED HERE AND READ BY THE RELAY AT THE START OF EACH ROUND. The phone is
  // never told which section it is; it is told a title to print. It cannot choose one.
  const [phoneDictation, setPhoneDictation] = useState(null);
  // The relay reads this as a round begins, so it must not lag a render behind the state.
  const phoneDictationRef = useRef(null);
  const [phoneDictationText, setPhoneDictationText] = useState("");
  /**
   * The section's text as it stands RIGHT NOW, which is what the next round appends to.
   *
   * ⚠️ NOT `armed.seed`, AND THAT DISTINCTION IS THE WHOLE BUG IT FIXES. The seed is captured
   * once, when the editor hands the section to the phone, and never moves again. Tapping record
   * a second time without closing the editor therefore stitched round two onto the text as it
   * was BEFORE round one, and round one vanished. It looked intermittent because closing and
   * reopening the editor re-arms with a fresh seed, so the same gesture appended or replaced
   * depending on something the clinician had no reason to connect to it.
   */
  const phoneDictationTextRef = useRef("");

  const getRoundTarget = useCallback(() => {
    const armed = phoneDictationRef.current;
    return armed
      // No `seed` on the target on purpose: a round's base is read live from
      // phoneDictationTextRef, and carrying a stale copy here is what made a second round
      // overwrite the first.
      ? { kind: "section", key: armed.key, title: armed.title }
      : { kind: "encounter" };
  }, []);

  const getRoundBase = useCallback(
    (target) =>
      target?.kind === "section"
        ? phoneDictationTextRef.current || ""
        : recordingHook.getFinalTranscript(),
    [recordingHook],
  );

  const routeRoundTranscript = useCallback(
    (text, target) => {
      if (target?.kind === "section") {
        phoneDictationTextRef.current = text;
        setPhoneDictationText(text);
      } else recordingHook.updateTranscript(text);
    },
    [recordingHook],
  );

  /**
   * ⚠️ A SECTION ROUND MUST NEVER REACH finishCapture, which runs the differential.
   *
   * An addendum to one paragraph of a note is an edit, not a new encounter. Letting it through
   * would re-run the DDx, the treatment plan and the note itself off the back of "add that the
   * wound was clean and dry" — silently, and with no way for the clinician to tell it happened.
   */
  const routeRoundFinished = useCallback((text, { target } = {}) => {
    if (target?.kind === "section") {
      // The ref matters more than the state here: the next round reads it as it begins, which
      // can be before React has re-rendered.
      phoneDictationTextRef.current = text;
      setPhoneDictationText(text);
      return;
    }
    finishCaptureRef.current?.(text);
  }, []);

  const phoneRelay = usePhoneRecordingRelay({
    ownerUid: user?.uid,
    language: recordingLanguage,
    getRoundTarget,
    // The encounter as it stands when a round begins, or the section's existing text for a
    // dictation. The wire carries only the round, so this is what it gets stitched onto —
    // without it, tapping record again on the phone would replace the first half with the second.
    getBaseTranscript: getRoundBase,
    onTranscript: routeRoundTranscript,
    onFinished: routeRoundFinished,
  });

  // Pulled out as their own bindings so effects can depend on the two things that actually
  // change rather than on `phoneRelay`, which is a fresh object every render — depending on the
  // object would fire the questions push below on every keystroke of the encounter.
  const { isPhoneRecording, pushClarifyingQuestions } = phoneRelay;

  /**
   * True while the phone is recording an ENCOUNTER round, false while it is dictating a note
   * section.
   *
   * ⚠️ THE DISTINCTION IS LOAD-BEARING IN TWO PLACES and both fail silently without it: a
   * dictation round would otherwise put the phone-remote's Next behind "please stop the
   * recording first", and — worse — start the clarifying-questions poller against the encounter
   * transcript while the clinician is editing a finished note, billing LLM calls and pushing a
   * previous phase's questions onto the phone mid-sentence.
   */
  const phoneEncounterRecording = isPhoneRecording && !phoneDictation;

  /**
   * Arm the paired phone to dictate into one note section.
   *
   * `seed` is the text already in the section, which becomes the round base — so speaking ADDS
   * to the section rather than replacing it, exactly like desktop dictation.
   */
  const beginPhoneDictation = useCallback((key, title, seed = "") => {
    const armed = { key, title, seed };
    // Ref first and synchronously: the relay reads it as the round begins, which can be the very
    // next snapshot, and state would still be a render behind.
    phoneDictationRef.current = armed;
    setPhoneDictation(armed);
    phoneDictationTextRef.current = seed;
    setPhoneDictationText(seed);
  }, []);

  /**
   * Disarm it — the editor closed, saved or cancelled.
   *
   * ⚠️ STOP A ROUND THAT IS STILL RUNNING. Without this the phone keeps recording into a dialog
   * that is no longer on screen. The round's own target was captured at its start, so its text
   * still routes harmlessly to the section sink rather than into the encounter.
   */
  const cancelPhoneDictation = useCallback(() => {
    const wasArmed = phoneDictationRef.current;
    phoneDictationRef.current = null;
    setPhoneDictation(null);
    phoneDictationTextRef.current = "";
    setPhoneDictationText("");
    if (wasArmed && isPhoneRecording) phoneRelay.requestStop();
  }, [isPhoneRecording, phoneRelay]);

  /**
   * True while EITHER device is capturing.
   *
   * Read this, not recordingHook.isRecording, for any question of the form "is an encounter
   * being captured right now". recordingHook.isRecording answers the narrower question "does
   * THIS device own a microphone", which is what the mic button and the transcript field need.
   */
  const captureActive = recordingHook.isRecording || phoneEncounterRecording;

  // ⚠️ `isRecording` here means "text is still arriving, keep polling", NOT "this device owns a
  // microphone" — two different questions wearing one name. During a phone recording the desktop
  // owns no mic, so passing recordingHook.isRecording would silently switch clarifying questions
  // off for the entire phone flow. `captureActive` is defined just below the relay hook.
  const clarifyingQuestionsHook = useClarifyingQuestions({
    transcript: recordingHook.transcript,
    isRecording: recordingHook.isRecording || phoneEncounterRecording,
    enabled: clarifyingQuestionsEnabled,
    patientContext: patientContextPayload,
    // The encounter's language, so the questions come back in it rather than in whatever
    // language a garbled second of audio happened to be transcribed as.
    language: recordingLanguage,
  });
  // Inpatient encounters use the inpatient prompts, falling back to these when an environment
  // has not been seeded with them yet (scripts/seedInpatientPrompts.js). Events Diagnosis uses
  // a specialty-specific prompt with no fallback (scripts/seedInpatientPrompts.js).
  const diagnosisHook = useChartMind({
    onDataChanged: stableOnDataChanged,
    ...(eventsNote
      ? eventsDiagnosisPromptOptions(specialty)
      : promptOptions("chartmind-diagnosis", encounterSetting, specialty)),
  });
  // Pending Daily Plan items, read when the note comes back (kept in a ref because the Daily Plan
  // state is declared below this hook).
  const pendingDailyPlanRef = useRef([]);
  // Events Objective rebuild inputs (events / kept exams / results) — ref so finalizeSections
  // can overwrite model objective after generation without re-creating the chart hook.
  const eventsObjectiveRef = useRef({
    events: [],
    examFindings: [],
    examResults: new Map(),
    examMode: "exam",
    exam2Systems: [],
  });
  const finalizeInpatientNote = useCallback(
    (sections) => ensurePendingInNote(sections, pendingDailyPlanRef.current),
    [],
  );
  const finalizeEventsNote = useCallback((sections) => {
    const { events, examFindings, examResults, examMode, exam2Systems } =
      eventsObjectiveRef.current;
    const objective = buildEventsObjectiveText({
      events,
      examFindings,
      examResults,
      examMode,
      exam2Systems,
    });
    if (!objective) return sections;
    return { ...sections, objective };
  }, []);
  const noteTemplate = useMemo(
    () => noteTemplateFor(specialty, encounterSetting, noteFormat),
    [specialty, encounterSetting, noteFormat],
  );
  const chartHook = useChartGeneration({
    template: noteTemplate,
    // Events chart is owned by chartmind-notes-events; do not send a specialty outline.
    documentationOutline: eventsNote
      ? undefined
      : formatDocumentationOutline(noteTemplate, { setting: encounterSetting, noteFormat }),
    finalizeSections: eventsNote
      ? finalizeEventsNote
      : inpatient
        ? finalizeInpatientNote
        : undefined,
    onDataChanged: stableOnDataChanged,
    ...(eventsNote
      ? {
          promptId: EVENTS_NOTE_PROMPT_ID,
          flexibleSections: true,
          diagnosesNamesOnly: true,
        }
      : promptOptions("chartmind-notes", encounterSetting, specialty)),
    fallbackInstructions: inpatient && !eventsNote ? INPATIENT_FALLBACK_INSTRUCTIONS : undefined,
  });
  // Specialty × setting focus only (no inpatient twin prompt id yet — base chartmind-plan + focus).
  const diagnosticPlanHook = useDiagnosticPlan({
    onDataChanged: stableOnDataChanged,
    focus: specialtyFocus("chartmind-plan", specialty, encounterSetting),
  });
  const confirmDiagnosisHook = useConfirmDiagnosis({ onDataChanged: stableOnDataChanged });
  // Events Plan uses the base treatment prompt; the inpatient one is the Daily Plan.
  const treatmentPlanHook = useTreatmentPlan({
    onDataChanged: stableOnDataChanged,
    ...(eventsNote ? {} : promptOptions("chartmind-treatment", encounterSetting, specialty)),
  });
  const dischargeHook = useChartMindDischarge({
    onDataChanged: stableOnDataChanged,
    ...promptOptions("chartmind-discharge", encounterSetting, specialty),
  });
  
  // Debounce transcript for manual text input (1.5s after typing stops)
  const [debouncedTranscript] = useDebounce(recordingHook.transcript, 1500);
  const reportingMetadata = useMemo(
    () => ({
      organizationId: organizationId || null,
      isSimulation: Boolean(isGlobalAdmin),
    }),
    [organizationId, isGlobalAdmin],
  );

  // ============================================================================
  // SESSION STATE
  // ============================================================================
  
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!initialSessionId);
  const [sessionError, setSessionError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasLoadedSession, setHasLoadedSession] = useState(false); // Track if we've loaded the initial session
  const [lastSavedAt, setLastSavedAt] = useState(null); // Timestamp of last successful save
  
  // If we're loading a session on mount, start with hydrating flag true
  const [isInitialLoad] = useState(!!initialSessionId);
  
  // ============================================================================
  // AUTO-SAVE STATE
  // ============================================================================
  
  // Track last saved snapshot for change detection
  const lastSavedSnapshotRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const autoSaveSessionRef = useRef(null);
  const isSavingRef = useRef(false); // Prevent concurrent saves
  const isHydratingRef = useRef(isInitialLoad); // Start true if loading session on mount
  // Always-current ref for serializeSession — avoids stale closure inside loadSession's setTimeout
  const serializeSessionRef = useRef(null);

  // ============================================================================
  // LIFT LOCAL STATE (previously in ChartMindPage)
  // ============================================================================
  
  // Setup shell first (mic or Continue). The note-type stepper is hidden until the user starts.
  const [encounterStarted, setEncounterStarted] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.RECORD);
  const [visitedSteps, setVisitedSteps] = useState(new Set([STEPS.RECORD]));

  const beginEncounter = useCallback((step = STEPS.RECORD) => {
    setEncounterStarted(true);
    setCurrentStep(step);
    setVisitedSteps(new Set([step]));
  }, []);

  const setNoteFormat = useCallback(
    (value) => {
      const format = normalizeNoteFormat(value);
      const wasEvents = isEventsNoteFormat(noteFormat);
      const nowEvents = isEventsNoteFormat(format);
      setNoteFormatState(format);
      try {
        window.localStorage.setItem(NOTE_FORMAT_KEY, format);
      } catch {
        // ignore
      }
      // Inpatient note types (Admission / Progress / Events) all lean *2. Reset empty *2
      // state when Events ↔ Admission/Progress flips so modes don't carry stale values.
      if (nowEvents !== wasEvents) {
        setExamMode("exam2");
        setPlan2ByCategory(createEmptyPlan2ByCategory("table"));
        setTreatment2ByCategory(createEmptyTreatment2ByCategory("table"));
      }
      // On the setup shell, note type only swaps the primary control (mic ↔ Continue).
      if (!encounterStarted) return;
      // After start: land on each note type's first tab — never skip ahead into Diagnosis/Plan.
      if (nowEvents && !wasEvents) {
        setCurrentStep(STEPS.EVENTS);
        setVisitedSteps(new Set([STEPS.EVENTS]));
      } else if (!nowEvents && wasEvents) {
        setCurrentStep(STEPS.RECORD);
        setVisitedSteps(new Set([STEPS.RECORD]));
      }
    },
    [noteFormat, encounterStarted],
  );
  
  // Diagnosis state
  const [selectedDiagnoses, setSelectedDiagnoses] = useState([]);
  /** User-added diagnoses from "Add Diagnosis" — must live in session for save/hydrate */
  const [customDiagnoses, setCustomDiagnoses] = useState([]);
  const [disabledDiagnosisIds, setDisabledDiagnosisIds] = useState(new Set());
  /**
   * What the clinician thought it was BEFORE the differential was shown to them.
   *
   * Null means they have not answered yet, and that is what gates the Diagnosis step — see
   * ClinicianDiagnosisGate. { condition: "", undetermined: true } is a real recorded answer, not an
   * empty one, so it opens the gate like any other.
   */
  const [clinicianImpression, setClinicianImpression] = useState(null);
  
  // Diagnostic plan state
  const [disabledTestIds, setDisabledTestIds] = useState(new Set());
  const [disabledExamIds, setDisabledExamIds] = useState(new Set());
  /** Events Objective: per-exam Normal / abnormal finding. */
  const [examResults, setExamResults] = useState(new Map());
  /** Exam (keep/hide) vs Exam 2 (systems table). Inpatient note types lean *2 by default. */
  const [examMode, setExamMode] = useState(() => (inpatient ? "exam2" : "exam"));
  const [exam2Systems, setExam2Systems] = useState(() => createBaseExam2Systems(null));
  /** Clinician Exam2 preferred normals (localStorage), keyed by system id. */
  const [exam2PreferredNormals, setExam2PreferredNormals] = useState({});
  const [testResults, setTestResults] = useState(new Map());
  /** Plan2 mode state keyed by diagnostic category. Inpatient: table (*2); outpatient: list. */
  const [plan2ByCategory, setPlan2ByCategory] = useState(() =>
    createEmptyPlan2ByCategory(inpatient ? "table" : "list"),
  );

  const exam2LayoutScope = useMemo(
    () => ({
      specialty,
      setting: encounterSetting,
      inpatient,
      noteFormat,
    }),
    [specialty, encounterSetting, inpatient, noteFormat],
  );

  // Load scoped layout prefs (order/options/hidden/preferred) whenever clinician or scope changes.
  useEffect(() => {
    const prefs = loadExam2LayoutPrefs(user?.uid, exam2LayoutScope);
    const fromLayout = preferredNormalsFromLayoutPrefs(prefs);
    const legacy = loadExam2PreferredNormals(user?.uid);
    setExam2PreferredNormals({ ...legacy, ...fromLayout });
    setExam2Systems((prev) => applyExam2LayoutPrefs(prev, prefs));
  }, [user?.uid, exam2LayoutScope]);

  // When sex becomes known, upgrade the placeholder GENERAL normal if it was never edited.
  useEffect(() => {
    const sexKey = extractPatientSexKey(patientContext);
    if (!sexKey) return;
    const nextGeneral = defaultGeneralFinding(sexKey);
    setExam2Systems((prev) =>
      prev.map((row) => {
        if (row.id !== "general") return row;
        const values = normalizeExam2Values(row.value);
        if (values.length === 1 && values[0] === "WDWN adult in NAD") {
          return { ...row, value: [nextGeneral] };
        }
        return row;
      }),
    );
  }, [patientContext]);
  
  // Confirmed diagnosis state
  const [selectedConfirmedDiagnoses, setSelectedConfirmedDiagnoses] = useState([]);
  
  // Treatment state
  const [customTreatments, setCustomTreatments] = useState([]);
  const [disabledTreatmentIds, setDisabledTreatmentIds] = useState(new Set());
  const [selectedTreatments, setSelectedTreatments] = useState(new Set());
  /** Treatment2 table mode: medication / therapy / treatmentsBundle. */
  const [treatment2ByCategory, setTreatment2ByCategory] = useState(() =>
    createEmptyTreatment2ByCategory(inpatient ? "table" : "list"),
  );

  // Inpatient state: clinician-entered events, and a comment or reassuring mark per Daily Plan
  // item (keyed by treatment id).
  const [inpatientEvents, setInpatientEvents] = useState([]);
  const [dailyPlanComments, setDailyPlanComments] = useState({});
  const [dailyPlanReassuring, setDailyPlanReassuring] = useState({});

  // Admission/Progress: *2 UI only (no list/Exam alternate). Keep session modes aligned so
  // note generation matches what the clinician sees.
  useEffect(() => {
    if (!inpatient || eventsNote) return;
    setExamMode((prev) => (prev === "exam2" ? prev : "exam2"));
    const forceTable = (prev) => {
      let changed = false;
      const next = {};
      Object.keys(prev || {}).forEach((key) => {
        const row = prev[key] || {};
        if (row.mode === "table") {
          next[key] = row;
          return;
        }
        changed = true;
        next[key] = { ...row, mode: "table" };
      });
      return changed ? next : prev;
    };
    setPlan2ByCategory(forceTable);
    setTreatment2ByCategory(forceTable);
  }, [inpatient, eventsNote]);
  
  // UI behavior tracking refs (not serialized to DB)
  const userNavigatedRef = useRef(false);
  const analysisTriggeredRef = useRef(false);
  const generatedForRef = useRef(null);

  // ============================================================================
  // AUTO-ANALYSIS EFFECTS
  // ============================================================================

  // Patient context the differential currently on screen was generated with. Lets the
  // auto-analysis effect tell "we already have a differential" apart from "we already have
  // a differential FOR THIS PATIENT'S RECORD".
  const ddxPatientContextKeyRef = useRef("");
  // True while a pasted transcript is being clarified in the background. The differential waits
  // for the clarified text instead of running on the raw paste.
  const [pasteClarifying, setPasteClarifying] = useState(false);
  // The Visit type is part of the key too: it picks the differential's prompt, and it can still
  // change on the Record step after the background differential has run.
  const ddxInputsKey = `${patientContextPayloadKey(patientContextPayload)}|${encounterSetting}|${specialty}|${noteFormat}`;

  const runDdx = useCallback((transcript) => {
    ddxPatientContextKeyRef.current = ddxInputsKey;
    diagnosisHook.analyzeDDX(transcript, patientContextPayload);
  }, [diagnosisHook.analyzeDDX, patientContextPayload, ddxInputsKey]);

  // Auto-analyze on debounced manual text input (when not recording).
  //
  // The hasDiagnoses / analysisTriggered guards exist to stop the differential regenerating
  // on every render and on a loaded session. They also used to block the one case that must
  // get through: a clinician who scans the patient's code AFTER the differential has been
  // produced. That left the differential pinned to what was known before the import, which
  // is the bug this whole change is about, so a changed patient record overrides them.
  // analyzeDDX still dedups internally, so an unchanged record cannot cause a re-run.
  useEffect(() => {
    // Events note type runs the differential from the Event step's continue handler, not from
    // a narrative transcript.
    if (eventsNote) return;

    const ddxMissesPatientContext =
      diagnosisHook.hasDiagnoses && ddxPatientContextKeyRef.current !== ddxInputsKey;

    // ⚠️ captureActive, NOT recordingHook.isRecording. A phone recording streams the transcript
    // in while this device's mic is idle, so keying on the local mic alone let the FIRST ~50
    // characters to arrive fire the whole differential 1.5s later — on two sentences of an
    // encounter — after which hasDiagnoses pinned it there for good. The clinician finished a
    // twelve-minute encounter and found a differential built from "so what brings you in today,
    // my knee has been". finishCapture runs the real one when the recording actually ends.
    if (
      !captureActive &&
      !pasteClarifying &&
      // Settled text only. The debounce lags a moment behind the field, so right after a paste
      // is clarified it still holds the raw paste, which is not what should be analyzed.
      debouncedTranscript === recordingHook.transcript &&
      debouncedTranscript &&
      debouncedTranscript.length >= 50 &&
      !diagnosisHook.loading &&
      !diagnosisHook.error && // Don't auto-retry after a failed call — user must retry manually
      (!analysisTriggeredRef.current || ddxMissesPatientContext) &&
      (!diagnosisHook.hasDiagnoses || ddxMissesPatientContext)
    ) {
      runDdx(debouncedTranscript);
    }
  }, [
    eventsNote,
    debouncedTranscript,
    recordingHook.transcript,
    captureActive,
    pasteClarifying,
    diagnosisHook.loading,
    diagnosisHook.error,
    diagnosisHook.hasDiagnoses,
    ddxInputsKey,
    runDdx,
  ]);

  // Reset analysis trigger when the clinical source changes significantly
  useEffect(() => {
    analysisTriggeredRef.current = false;
  }, [recordingHook.transcript, inpatientEvents, noteFormat]);

  // ============================================================================
  // HANDLERS (lifted from ChartMindPage)
  // ============================================================================

  /**
   * Handle mic button click
   */
  /**
   * Everything that has to happen when a recording ends, wherever it was recorded.
   *
   * Extracted because a phone recording has to land in exactly the same state a desktop one
   * does. Without this, a phone encounter arrived on the Record step with no differential and
   * the flow the client asked for stalled at step one.
   */
  const finishCapture = useCallback((finalTranscript) => {
    userNavigatedRef.current = false;
    analysisTriggeredRef.current = true;
    if (finalTranscript && finalTranscript.length >= 50) {
      runDdx(finalTranscript);
    }
  }, [runDdx]);

  useEffect(() => {
    finishCaptureRef.current = finishCapture;
  }, [finishCapture]);

  // Push clarifying questions down to the phone while it is the device recording.
  //
  // The DESKTOP writes them, because it is already polling for them. Running a second
  // useClarifyingQuestions on the phone would bill a second set of LLM calls for one encounter.
  // The phone is the screen actually in the clinician's hand, so it is the right place to show
  // them — and this is the only field the desktop writes on the relay, which keeps the
  // one-writer-per-field rule the security rules are built around.
  // ⚠️ ENCOUNTER ROUNDS ONLY. A phone dictating a note section is not taking a history, and the
  // last encounter's questions appearing over a dictation screen would be answering a question
  // nobody asked.
  useEffect(() => {
    if (!phoneEncounterRecording) return;
    pushClarifyingQuestions(clarifyingQuestionsHook.questions);
  }, [clarifyingQuestionsHook.questions, phoneEncounterRecording, pushClarifyingQuestions]);

  const handleMicClick = useCallback(async () => {
    if (recordingHook.isRecording) {
      await recordingHook.stopRecording();
      // Read from the ref directly — React state may not have flushed recognition.onend yet
      finishCapture(recordingHook.getFinalTranscript());
    } else {
      // Mic is the Admission/Progress start control — enter the Narrative stepper, then record.
      if (!encounterStarted) beginEncounter(STEPS.RECORD);
      await recordingHook.startRecording();
    }
  }, [
    recordingHook.isRecording,
    recordingHook.stopRecording,
    recordingHook.startRecording,
    recordingHook.getFinalTranscript,
    finishCapture,
    encounterStarted,
    beginEncounter,
  ]);

  /** Events note type: Continue enters the Event → Diagnosis → … stepper. */
  const handleContinueToEventSequence = useCallback(() => {
    beginEncounter(STEPS.EVENTS);
    autoSaveSessionRef.current?.("handleContinueToEventSequence");
  }, [beginEncounter]);

  // Paste / test-fixture transcripts enter the Narrative stepper without pressing the mic.
  useEffect(() => {
    if (encounterStarted || eventsNote) return;
    if (recordingHook.transcript && recordingHook.transcript.trim().length >= 50) {
      beginEncounter(STEPS.RECORD);
    }
  }, [recordingHook.transcript, encounterStarted, eventsNote, beginEncounter]);

  /**
   * Handle step change (manual navigation)
   */
  const handleStepChange = useCallback((step) => {
    userNavigatedRef.current = true;
    setCurrentStep(step);
    // Unlock Diagnosis when manually navigating to it
    if (step === STEPS.DIAGNOSIS && recordingHook.transcript) {
      setVisitedSteps(prev => new Set([...prev, STEPS.DIAGNOSIS]));
    }
    // Auto-save on navigation (only if data changed)
    autoSaveSessionRef.current?.('handleStepChange');
  }, [recordingHook.transcript]);

  /**
   * Handle diagnosis selection change
   */
  const handleSelectionChange = useCallback((newSelection) => {
    setSelectedDiagnoses(newSelection);
  }, []);

  /**
   * Events note type: the working differential is every diagnosis that is not excluded.
   * No click-to-select — DDX (and customs) all start kept; the eye is the only filter.
   */
  useEffect(() => {
    if (!eventsNote) return;

    const all = [
      ...(diagnosisHook.ddxData?.primary_diagnoses || []),
      ...(diagnosisHook.ddxData?.alternative_diagnoses || []),
      ...customDiagnoses,
    ];
    const seen = new Set();
    const kept = [];
    for (const dx of all) {
      const condition = dx?.condition;
      if (!condition || seen.has(condition) || disabledDiagnosisIds.has(condition)) {
        continue;
      }
      seen.add(condition);
      kept.push(dx);
    }

    setSelectedDiagnoses((prev) => {
      const prevKey = prev.map((d) => d.condition).join("\0");
      const nextKey = kept.map((d) => d.condition).join("\0");
      return prevKey === nextKey ? prev : kept;
    });
  }, [
    eventsNote,
    diagnosisHook.ddxData,
    customDiagnoses,
    disabledDiagnosisIds,
  ]);

  /**
   * Add a custom diagnosis from the modal and select it so session snapshot updates (auto-save).
   * Events: append only — the keep/hide sync effect owns selectedDiagnoses.
   */
  const handleAddCustomDiagnosis = useCallback((diagnosis) => {
    setCustomDiagnoses((prev) => [...prev, diagnosis]);
    if (eventsNote) return;
    setSelectedDiagnoses([diagnosis]);
  }, [eventsNote]);

  /**
   * Record the clinician's own diagnosis, given before they were shown the differential.
   *
   * It is stored twice on purpose. `clinicianImpression` is the answer as given, with its
   * timestamp, and is what the gate checks — nothing the clinician does later can walk it back
   * into "I never said that". The custom-diagnosis copy is the same answer as a usable card:
   * it selects, hides, saves and hydrates through the paths every other diagnosis already uses,
   * which is why this needed no new plumbing downstream.
   *
   * ⚠️ IT IS NOT SELECTED ON THEIR BEHALF. Answering the gate is not the same as deciding —
   * the client was explicit that a diagnosis they gave before seeing the differential is a
   * candidate, not something to hand a "Final Diagnosis" chip and a green light on Continue.
   * The clinician has to click a card, theirs or the model's, before anything counts as chosen.
   * An edit made *while their card is already selected* is the one exception: that is fixing
   * wording on a decision already made, not making a new one, so the selection follows it.
   */
  const handleRecordClinicianImpression = useCallback((impression) => {
    const condition = (impression?.condition || "").trim();
    const reasoning = (impression?.reasoning || "").trim();
    const now = formatISO(new Date());

    setClinicianImpression((prev) => {
      // ⚠️ THE FIRST ANSWER IS NOT OVERWRITTEN BY A LATER EDIT. Revising a working diagnosis
      // after reading the differential is ordinary practice and the encounter has to carry the
      // revision — but "what did you think before you saw the list" is the one question here
      // that can only be answered once, so the original stays on the record beside it.
      const firstCondition = prev
        ? prev.firstCondition || prev.condition || ""
        : condition;
      return {
        condition,
        reasoning,
        undetermined: Boolean(impression?.undetermined) || !condition,
        firstCondition,
        recordedAt: prev?.recordedAt || now,
        ...(prev && { revisedAt: now }),
      };
    });

    const diagnosis = condition
      ? {
          condition,
          ...(reasoning && { rationale: reasoning }),
          isCustom: true,
          isClinicianImpression: true,
        }
      : null;

    // An edit replaces the card rather than adding a second one, and takes the selection with
    // it — leaving the old wording selected would send the superseded diagnosis to the plan.
    setCustomDiagnoses((prev) => {
      const others = prev.filter((dx) => !dx.isClinicianImpression);
      return diagnosis ? [...others, diagnosis] : others;
    });
    setSelectedDiagnoses((prev) => {
      const wasOwnSelected = prev.some((dx) => dx.isClinicianImpression);
      if (!wasOwnSelected) return prev;
      return diagnosis ? [diagnosis] : [];
    });
  }, []);

  /**
   * When the model listed the clinician's diagnosis too, its entry becomes the one the
   * encounter carries.
   *
   * ⚠️ WHICH OBJECT IS SELECTED DECIDES WHAT THE REST OF THE ENCOUNTER IS BUILT FROM, and the
   * two copies are not equivalent. The model's entry has the canonical spelling and the
   * rationale that the treatment, discharge and note prompts read; the clinician's has their own
   * wording, typo and all. Leaving theirs selected sent "Influenzaa" to the chart note and threw
   * away the reasoning behind the ranking.
   *
   * ⚠️ NOTHING IS LOST BY THIS. Their wording and their findings live in clinicianImpression,
   * which is the record of what they said and is never rewritten here — the Diagnosis step shows
   * both sets of reasoning on the one card. This only removes the duplicate CARD.
   *
   * ⚠️ THIS DOES NOT SELECT ANYTHING THAT WASN'T ALREADY SELECTED. Swapping cards when nothing
   * is chosen yet would be the same auto-select the clinician's own answer no longer gets — the
   * differential landing is not a decision either. Only a card the clinician had already picked
   * follows the swap, so what is selected does not silently change out from under them.
   *
   * It runs as an effect rather than inside the handler because the differential often lands
   * after the answer: a clinician who types quickly answers while it is still generating.
   */
  useEffect(() => {
    const condition = clinicianImpression?.condition;
    if (!condition) return;

    const ddxEntries = [
      ...(diagnosisHook.ddxData?.primary_diagnoses || []),
      ...(diagnosisHook.ddxData?.alternative_diagnoses || []),
    ];
    const match = ddxEntries.find((dx) => isSameCondition(dx.condition, condition));
    if (!match) return;

    setCustomDiagnoses((prev) =>
      prev.some((dx) => dx.isClinicianImpression)
        ? prev.filter((dx) => !dx.isClinicianImpression)
        : prev,
    );
    setSelectedDiagnoses((prev) =>
      prev.some((dx) => dx.isClinicianImpression) ? [match] : prev,
    );
  }, [diagnosisHook.ddxData, clinicianImpression]);

  /**
   * Handle confirmed diagnosis selection change
   */
  const handleConfirmedSelectionChange = useCallback((newSelection) => {
    setSelectedConfirmedDiagnoses(newSelection);
  }, []);

  /**
   * Toggle diagnosis disabled state
   */
  const handleToggleDiagnosisDisabled = useCallback((diagnosisId) => {
    setDisabledDiagnosisIds((prev) => {
      const next = new Set(prev);
      if (next.has(diagnosisId)) {
        next.delete(diagnosisId);
      } else {
        next.add(diagnosisId);
      }
      return next;
    });
  }, []);

  /**
   * Toggle diagnostic test disabled state
   */
  const handleToggleTestDisabled = useCallback((testId) => {
    setDisabledTestIds((prev) => {
      const next = new Set(prev);
      if (next.has(testId)) {
        next.delete(testId);
      } else {
        next.add(testId);
      }
      return next;
    });
  }, []);

  /** Events Plan: keep/hide exam findings from chartmind-plan. */
  const handleToggleExamDisabled = useCallback((examId) => {
    setDisabledExamIds((prev) => {
      const next = new Set(prev);
      if (next.has(examId)) next.delete(examId);
      else next.add(examId);
      return next;
    });
  }, []);

  /**
   * Replace all exam results and rewrite Events Objective from the structured source of truth.
   * Used by the Objective edit dialog Save (Exam mode).
   */
  const handleSaveObjectiveExamResults = useCallback(
    ({ examResults: nextResults, objective }) => {
      setExamResults(nextResults instanceof Map ? nextResults : new Map());
      if (eventsNote && typeof objective === "string" && chartHook.hasNote) {
        chartHook.updateSection("objective", objective);
      }
    },
    [eventsNote, chartHook],
  );

  /**
   * Save Exam2 Objective edits from the Chart dialog (full systems snapshot + objective text).
   */
  const handleSaveObjectiveExam2 = useCallback(
    ({ exam2Systems: nextSystems, objective }) => {
      if (Array.isArray(nextSystems)) setExam2Systems(nextSystems);
      if (eventsNote && typeof objective === "string" && chartHook.hasNote) {
        chartHook.updateSection("objective", objective);
      }
    },
    [eventsNote, chartHook],
  );

  const handleExamModeChange = useCallback(
    (nextMode) => {
      const mode = nextMode === "exam2" ? "exam2" : "exam";
      setExamMode(mode);
      if (!eventsNote || !chartHook.hasNote) return;
      const keptExams = (diagnosticPlanHook.planData?.examFindings || []).filter(
        (finding) => !disabledExamIds.has(finding.id),
      );
      const objective = buildEventsObjectiveText({
        events: inpatientEvents,
        examFindings: keptExams,
        examResults,
        examMode: mode,
        exam2Systems,
      });
      if (objective) chartHook.updateSection("objective", objective);
    },
    [
      eventsNote,
      chartHook,
      diagnosticPlanHook.planData,
      disabledExamIds,
      inpatientEvents,
      examResults,
      exam2Systems,
    ],
  );

  const handleMakeExam2DefaultNormal = useCallback(
    (systemId, finding) => {
      const text = String(finding || "").trim();
      if (!systemId || !text) return;
      const nextPrefs = setExam2PreferredNormal(user?.uid, systemId, text);
      setExam2PreferredNormals(nextPrefs);
      setExam2Systems((prev) => {
        const nextSystems = prev.map((row) => {
          if (row.id !== systemId) return row;
          const options = [...new Set([...(row.options || []), text])];
          const normalOptions = [...new Set([...(row.normalOptions || []), text])];
          return { ...row, options, normalOptions, preferredNormal: text };
        });
        // Keep scoped layout prefs in sync with star-as-default.
        const layout = systemsToExam2LayoutPrefs(nextSystems, nextPrefs);
        saveExam2LayoutPrefs(user?.uid, exam2LayoutScope, layout);
        return nextSystems;
      });
    },
    [user?.uid, exam2LayoutScope],
  );

  /** Replace Exam2 systems for this encounter only (Customize → This note). */
  const handleReplaceExam2Systems = useCallback(
    (nextSystems) => {
      if (!Array.isArray(nextSystems)) return;
      const preferred = {};
      nextSystems.forEach((row) => {
        const text = String(row?.preferredNormal || "").trim();
        if (row?.id && text) preferred[row.id] = text;
      });
      setExam2PreferredNormals((prev) => ({ ...prev, ...preferred }));
      setExam2Systems(
        nextSystems.map((row) => ({
          ...row,
          options: [...(row.options || [])],
          normalOptions: [...(row.normalOptions || [])],
          value: normalizeExam2Values(row.value),
          hidden: Boolean(row.hidden),
        })),
      );
      if (eventsNote && examMode === "exam2" && chartHook.hasNote) {
        const objective = buildEventsObjectiveText({
          events: inpatientEvents,
          examFindings: [],
          examResults,
          examMode: "exam2",
          exam2Systems: nextSystems,
        });
        if (objective) chartHook.updateSection("objective", objective);
      }
    },
    [eventsNote, examMode, chartHook, inpatientEvents, examResults],
  );

  /** Save Customize → My defaults for this specialty × setting × note type, and apply now. */
  const handleSaveExam2LayoutDefaults = useCallback(
    (nextSystems) => {
      if (!Array.isArray(nextSystems)) return;
      const preferred = {};
      nextSystems.forEach((row) => {
        const text = String(row?.preferredNormal || "").trim();
        if (row?.id && text) preferred[row.id] = text;
      });
      const layout = systemsToExam2LayoutPrefs(nextSystems, preferred);
      saveExam2LayoutPrefs(user?.uid, exam2LayoutScope, layout);
      setExam2PreferredNormals((prev) => ({ ...prev, ...preferred }));
      handleReplaceExam2Systems(nextSystems);
    },
    [user?.uid, exam2LayoutScope, handleReplaceExam2Systems],
  );

  /** Fill every Exam2 system with its preferred normal (clinician / sex-aware / catalog). */
  const handleCreateNormalExam2 = useCallback(() => {
    const sexKey = extractPatientSexKey(patientContext);
    setExam2Systems((prev) => {
      const next = prev.map((row) => {
        const preferred = getPreferredExam2Normal(row, {
          sexKey,
          preferredBySystemId: exam2PreferredNormals,
        });
        if (!preferred) return row;
        return { ...row, value: [preferred] };
      });
      if (eventsNote && examMode === "exam2" && chartHook.hasNote) {
        const objective = buildEventsObjectiveText({
          events: inpatientEvents,
          examFindings: [],
          examResults,
          examMode: "exam2",
          exam2Systems: next,
        });
        if (objective) chartHook.updateSection("objective", objective);
      }
      return next;
    });
  }, [
    patientContext,
    exam2PreferredNormals,
    eventsNote,
    examMode,
    chartHook,
    inpatientEvents,
    examResults,
  ]);

  const handleChangeExam2SystemValue = useCallback(
    (systemId, value) => {
      setExam2Systems((prev) => {
        const nextValues = Array.isArray(value)
          ? value.map((item) => String(item || "").trim()).filter(Boolean)
          : String(value || "")
              .split(/\s*;\s*/)
              .map((item) => item.trim())
              .filter(Boolean);
        const next = prev.map((row) =>
          row.id === systemId ? { ...row, value: nextValues } : row,
        );
        if (eventsNote && examMode === "exam2" && chartHook.hasNote) {
          const objective = buildEventsObjectiveText({
            events: inpatientEvents,
            examFindings: [],
            examResults,
            examMode: "exam2",
            exam2Systems: next,
          });
          if (objective) chartHook.updateSection("objective", objective);
        }
        return next;
      });
    },
    [eventsNote, examMode, chartHook, inpatientEvents, examResults],
  );

  /** Document Normal / abnormal for one exam (e.g. future inline edits). */
  const handleUpdateExamResult = useCallback(
    (examId, result) => {
      const keptExams = (diagnosticPlanHook.planData?.examFindings || []).filter(
        (finding) => !disabledExamIds.has(finding.id),
      );
      setExamResults((prev) => {
        const next = new Map(prev);
        if (!result || !result.status) next.delete(examId);
        else next.set(examId, result);
        if (eventsNote && examMode === "exam" && chartHook.hasNote) {
          const objective = buildEventsObjectiveText({
            events: inpatientEvents,
            examFindings: keptExams,
            examResults: next,
            examMode: "exam",
          });
          if (objective) chartHook.updateSection("objective", objective);
        }
        return next;
      });
    },
    [
      eventsNote,
      examMode,
      diagnosticPlanHook.planData,
      disabledExamIds,
      inpatientEvents,
      chartHook,
    ],
  );

  /**
   * Update test result
   */
  const updatePlan2Category = useCallback((categoryKey, updater) => {
    setPlan2ByCategory((prev) => {
      const current = prev[categoryKey] || createEmptyPlan2ByCategory()[categoryKey];
      const nextState = typeof updater === "function" ? updater(current) : updater;
      if (nextState === current) return prev;
      return { ...prev, [categoryKey]: nextState };
    });
  }, []);

  const handlePlan2ModeChange = useCallback(
    (categoryKey, mode, itemIds = []) => {
      updatePlan2Category(categoryKey, (current) => {
        let next = { ...current, mode: mode === "table" ? "table" : "list" };
        if (mode === "table") next = ensurePlan2Defaults(next, itemIds);
        return next;
      });
    },
    [updatePlan2Category],
  );

  // Default *2 table mode: seed Ordered, pending when diagnostic tests appear.
  useEffect(() => {
    const tests = diagnosticPlanHook.planData?.tests || [];
    if (!tests.length) return;
    const byCat = {};
    tests.forEach((test) => {
      const cat = test.category || "other";
      if (!byCat[cat]) byCat[cat] = [];
      byCat[cat].push(test.id);
    });
    setPlan2ByCategory((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(byCat).forEach(([cat, ids]) => {
        const current = next[cat] || createEmptyPlan2ByCategory()[cat];
        if (current.mode !== "table") return;
        const seeded = ensurePlan2Defaults(current, ids);
        if (seeded !== current) {
          next[cat] = seeded;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [diagnosticPlanHook.planData?.tests]);

  const handlePlan2ItemValueChange = useCallback(
    (categoryKey, itemId, value) => {
      updatePlan2Category(categoryKey, (current) => ({
        ...current,
        values: {
          ...(current.values || {}),
          [itemId]: normalizePlan2Values(value),
        },
      }));
    },
    [updatePlan2Category],
  );

  const handlePlan2ItemFocus = useCallback(
    (categoryKey, itemId) => {
      updatePlan2Category(categoryKey, (current) => {
        const { state } = applyPlan2FocusFlip(current, itemId);
        return state;
      });
    },
    [updatePlan2Category],
  );

  const handlePlan2ItemBlur = useCallback(
    (categoryKey, itemId) => {
      updatePlan2Category(categoryKey, (current) => markPlan2PendingFlip(current, itemId));
    },
    [updatePlan2Category],
  );

  const handlePlan2DocumentNormal = useCallback(
    (categoryKey, itemIds = []) => {
      updatePlan2Category(categoryKey, (current) =>
        usesMarkAllReassuring(categoryKey)
          ? fillPlan2ReassuringFromPending(current, itemIds)
          : fillPlan2Normals(current, itemIds),
      );
    },
    [updatePlan2Category],
  );

  const updateTreatment2Category = useCallback((groupKey, updater) => {
    setTreatment2ByCategory((prev) => {
      const current = prev[groupKey] || createEmptyTreatment2ByCategory()[groupKey];
      const nextState = typeof updater === "function" ? updater(current) : updater;
      if (nextState === current) return prev;
      return { ...prev, [groupKey]: nextState };
    });
  }, []);

  const handleTreatment2ModeChange = useCallback(
    (groupKey, mode, itemIds = []) => {
      updateTreatment2Category(groupKey, (current) => {
        let next = { ...current, mode: mode === "table" ? "table" : "list" };
        if (mode === "table") {
          next = ensurePlan2Defaults(next, itemIds, { variant: "treatment" });
        }
        return next;
      });
    },
    [updateTreatment2Category],
  );

  const handleTreatment2ItemValueChange = useCallback(
    (groupKey, itemId, value) => {
      updateTreatment2Category(groupKey, (current) => ({
        ...current,
        values: {
          ...(current.values || {}),
          [itemId]: normalizePlan2Values(value),
        },
      }));
    },
    [updateTreatment2Category],
  );

  /** Treatment *2: no pending-flip (stays Ordered). */
  const handleTreatment2ItemFocus = useCallback(() => {}, []);
  const handleTreatment2ItemBlur = useCallback(() => {}, []);

  const handleTreatment2DocumentNormal = useCallback(
    (groupKey, itemIds = []) => {
      updateTreatment2Category(groupKey, (current) =>
        fillAllPlan2Ordered(current, itemIds),
      );
    },
    [updateTreatment2Category],
  );

  // Default *2 table mode: seed Ordered when treatments appear.
  useEffect(() => {
    const treatments = [
      ...(treatmentPlanHook.planData?.treatments || []),
      ...customTreatments,
    ];
    if (!treatments.length) return;
    const byGroup = { medication: [], therapy: [], treatmentsBundle: [] };
    treatments.forEach((treatment) => {
      const group =
        treatment.category === "medication"
          ? "medication"
          : treatment.category === "therapy"
            ? "therapy"
            : "treatmentsBundle";
      byGroup[group].push(treatment.id);
    });
    setTreatment2ByCategory((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(byGroup).forEach(([group, ids]) => {
        if (!ids.length) return;
        const current = next[group] || createEmptyTreatment2ByCategory()[group];
        if (current.mode !== "table") return;
        const seeded = ensurePlan2Defaults(current, ids, { variant: "treatment" });
        if (seeded !== current) {
          next[group] = seeded;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [treatmentPlanHook.planData?.treatments, customTreatments]);

  const handleUpdateTestResult = useCallback((testId, result) => {
    setTestResults((prev) => {
      const next = new Map(prev);
      if (result === null || result === undefined) {
        next.delete(testId);
      } else {
        next.set(testId, result);
      }
      return next;
    });
  }, []);

  /**
   * Add a custom treatment from the modal and auto-select it.
   * Events: append only — the keep/hide sync effect owns selectedTreatments.
   */
  const handleAddCustomTreatment = useCallback((treatment) => {
    setCustomTreatments((prev) => [...prev, treatment]);
    if (eventsNote) return;
    setSelectedTreatments((prev) => {
      const next = new Set(prev);
      next.add(treatment.id);
      return next;
    });
  }, [eventsNote]);

  /**
   * Toggle treatment disabled state
   */
  const handleToggleTreatmentDisabled = useCallback((treatmentId) => {
    setDisabledTreatmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(treatmentId)) {
        next.delete(treatmentId);
      } else {
        next.add(treatmentId);
        // Auto-deselect when disabling (Admission/Progress checkbox model).
        // Events keep/hide sync rewrites selectedTreatments from non-excluded ids.
        if (!eventsNote) {
          setSelectedTreatments((prevSelected) => {
            const nextSelected = new Set(prevSelected);
            nextSelected.delete(treatmentId);
            return nextSelected;
          });
        }
      }
      return next;
    });
  }, [eventsNote]);

  /**
   * Toggle treatment selected state
   */
  const handleToggleTreatmentSelected = useCallback((treatmentId) => {
    setSelectedTreatments((prev) => {
      const next = new Set(prev);
      if (next.has(treatmentId)) {
        next.delete(treatmentId);
      } else {
        next.add(treatmentId);
      }
      return next;
    });
  }, []);

  /**
   * Events note type: working treatments = every suggestion that is not excluded.
   * Same keep/hide model as Events Diagnosis — no checkbox click required.
   */
  useEffect(() => {
    if (!eventsNote) return;

    const all = [
      ...(treatmentPlanHook.planData?.treatments || []),
      ...customTreatments,
    ];
    const kept = new Set(
      all.filter((t) => t?.id && !disabledTreatmentIds.has(t.id)).map((t) => t.id),
    );

    setSelectedTreatments((prev) => {
      if (prev.size === kept.size && [...kept].every((id) => prev.has(id))) {
        return prev;
      }
      return kept;
    });
  }, [
    eventsNote,
    treatmentPlanHook.planData,
    customTreatments,
    disabledTreatmentIds,
  ]);

  const handleAddInpatientEvent = useCallback((name, comment = "") => {
    if (!String(name || "").trim()) return null;
    const event = createInpatientEvent(name, comment);
    setInpatientEvents((prev) => [...prev, event]);
    return event;
  }, []);

  const handleUpdateInpatientEvent = useCallback((eventId, { name, comment }) => {
    setInpatientEvents((prev) =>
      prev.map((event) =>
        event.id === eventId
          ? { ...event, ...(name ? { name } : {}), ...(comment !== undefined ? { comment } : {}) }
          : event,
      ),
    );
  }, []);

  const handleRemoveInpatientEvent = useCallback((eventId) => {
    setInpatientEvents((prev) => prev.filter((event) => event.id !== eventId));
  }, []);

  const handleDailyPlanCommentChange = useCallback((itemId, comment) => {
    setDailyPlanComments((prev) => {
      const next = { ...prev };
      if (String(comment || "").trim()) next[itemId] = comment;
      else delete next[itemId];
      return next;
    });
  }, []);

  const handleSetDailyPlanReassuring = useCallback((itemId, reassuring) => {
    setDailyPlanReassuring((prev) => {
      const next = { ...prev };
      if (reassuring) next[itemId] = true;
      else delete next[itemId];
      return next;
    });
  }, []);

  /**
   * Events note type: the clinician-entered events are the clinical source for every later step.
   */
  const eventsSourceText = useMemo(
    () => (eventsNote ? buildEventsSourceText(inpatientEvents) : ""),
    [eventsNote, inpatientEvents],
  );
  const clinicalSourceText = eventsNote ? eventsSourceText : recordingHook.transcript;
  const planSourceText = eventsNote
    ? `${eventsSourceText}\n\n${EVENTS_PLAN_FOCUS}`
    : recordingHook.transcript;

  /**
   * What an inpatient note is written from beyond the transcript and diagnoses. Empty for
   * outpatient, which leaves the outpatient note request exactly as it was. Events notes send
   * events plus chosen tests and treatments, with no Daily Plan or Pending block.
   */
  const inpatientNoteContext = useMemo(() => {
    if (!inpatient) return "";
    const checkedTreatments = [
      ...(treatmentPlanHook.planData?.treatments || []),
      ...customTreatments,
    ].filter((t) => selectedTreatments.has(t.id) && !disabledTreatmentIds.has(t.id));

    if (eventsNote) {
      pendingDailyPlanRef.current = [];
      const checkedExams = (diagnosticPlanHook.planData?.examFindings || []).filter(
        (finding) => !disabledExamIds.has(finding.id),
      );
      return buildEventsNoteContext({
        events: inpatientEvents,
        examFindings: checkedExams,
        examResults,
        examMode,
        exam2Systems,
        allTests: diagnosticPlanHook.planData?.tests || [],
        plan2ByCategory,
        disabledTestIds,
        testResults,
        allTreatments: [
          ...(treatmentPlanHook.planData?.treatments || []),
          ...customTreatments,
        ],
        treatment2ByCategory,
        selectedTreatmentIds: selectedTreatments,
        disabledTreatmentIds,
      });
    }

    pendingDailyPlanRef.current = pendingDailyPlanNames(checkedTreatments, {
      comments: dailyPlanComments,
      reassuring: dailyPlanReassuring,
    });
    const checkedExams = (diagnosticPlanHook.planData?.examFindings || []).filter(
      (finding) => !disabledExamIds.has(finding.id),
    );
    return buildInpatientNoteContext({
      checkedItems: checkedTreatments,
      comments: dailyPlanComments,
      reassuring: dailyPlanReassuring,
      events: inpatientEvents,
      examFindings: checkedExams,
      examResults,
      examMode,
      exam2Systems,
      allTests: diagnosticPlanHook.planData?.tests || [],
      plan2ByCategory,
      disabledTestIds,
      testResults,
    });
  }, [
    inpatient,
    eventsNote,
    noteFormat,
    treatmentPlanHook.planData,
    diagnosticPlanHook.planData,
    customTreatments,
    selectedTreatments,
    disabledTreatmentIds,
    disabledExamIds,
    examResults,
    examMode,
    exam2Systems,
    disabledTestIds,
    testResults,
    plan2ByCategory,
    treatment2ByCategory,
    dailyPlanComments,
    dailyPlanReassuring,
    inpatientEvents,
  ]);

  // Outpatient: same Exam / Diagnostics *2 serialization into the note request.
  const outpatientExamDiagnosticsContext = useMemo(() => {
    if (inpatient) return "";
    const checkedExams = (diagnosticPlanHook.planData?.examFindings || []).filter(
      (finding) => !disabledExamIds.has(finding.id),
    );
    return buildExamDiagnosticsNoteContext({
      examFindings: checkedExams,
      examResults,
      examMode,
      exam2Systems,
      allTests: diagnosticPlanHook.planData?.tests || [],
      plan2ByCategory,
      disabledTestIds,
      testResults,
    });
  }, [
    inpatient,
    diagnosticPlanHook.planData,
    disabledExamIds,
    examResults,
    examMode,
    exam2Systems,
    disabledTestIds,
    testResults,
    plan2ByCategory,
  ]);

  const noteEncounterDetails = inpatient
    ? { inpatient: inpatientNoteContext }
    : outpatientExamDiagnosticsContext
      ? { examDiagnostics: outpatientExamDiagnosticsContext }
      : null;

  // Keep finalizeEventsNote / Objective rebuilds in sync with the structured source of truth.
  useEffect(() => {
    if (!eventsNote) return;
    const examFindings = (diagnosticPlanHook.planData?.examFindings || []).filter(
      (finding) => !disabledExamIds.has(finding.id),
    );
    eventsObjectiveRef.current = {
      events: inpatientEvents,
      examFindings,
      examResults,
      examMode,
      exam2Systems,
    };
  }, [
    eventsNote,
    inpatientEvents,
    diagnosticPlanHook.planData,
    disabledExamIds,
    examResults,
    examMode,
    exam2Systems,
  ]);

  // Merge AI Exam2 extras into the base systems catalog; preserve clinician-entered values.
  useEffect(() => {
    if (!eventsNote) return;
    const extras = diagnosticPlanHook.planData?.exam2Extras;
    if (!Array.isArray(extras)) return;
    setExam2Systems((prev) => {
      const merged = mergeExam2Systems({
        patientContext,
        extras,
        previousSystems: prev,
      });
      return applyExam2LayoutPrefs(merged, loadExam2LayoutPrefs(user?.uid, exam2LayoutScope));
    });
  }, [
    eventsNote,
    diagnosticPlanHook.planData?.exam2Extras,
    patientContext,
    user?.uid,
    exam2LayoutScope,
  ]);

  /**
   * Create stable identifier for generation request
   */
  const getGenerationKey = useCallback((transcript, diagnoses) => {
    // Covers the WHOLE transcript and EVERY selected diagnosis. The old key was
    // `transcript.slice(0, 100)` plus the first diagnosis, so the common "go back and add more
    // detail" edit landed past character 100, left the key unchanged, and the note was silently
    // treated as current. djb2 keeps the key short without dropping any input.
    // The patient context is part of the key so importing a record after a note exists both
    // marks the note stale in the banner and forces a regenerate rather than serving the
    // note that was written without the record.
    const conditions = (diagnoses || [])
      .map((d) => d?.condition || d?.name || '')
      .sort()
      .join(',');
    // Inpatient adds the Daily Plan, Events and note type, so editing any of them marks the note
    // stale.
    const inpatientKey = inpatient ? `|${djb2(inpatientNoteContext)}|${noteFormat}` : '';
    // A different specialty writes a different note.
    const specialtyKey = `|${specialty}`;
    return `${djb2(transcript || '')}:${(transcript || '').length}|${djb2(conditions)}|${patientContextPayloadKey(patientContextPayload)}${inpatientKey}${specialtyKey}`;
  }, [patientContextPayload, inpatient, inpatientNoteContext, noteFormat, specialty]);

  /**
   * Prefetch note generation (called after debounce when diagnosis selected)
   * DISABLED: This was causing premature note generation before discharge step
   */
  const handlePrefetch = useCallback(async (diagnoses) => {
    // Prefetch disabled - note will only generate when user clicks "Generate Note" button
    // if (diagnoses.length > 0 && recordingHook.transcript) {
    //   const genKey = getGenerationKey(recordingHook.transcript, diagnoses);
    //   generatedForRef.current = genKey;
    //   await chartHook.generateNote(recordingHook.transcript, diagnoses);
    // }
  }, [currentStep]);

  /**
   * Continue to Diagnosis step.
   * @param {{ events?: Array }} [options] - When advancing right after Add Event, pass the
   *   events list that includes the new row so DDX does not race the state update.
   */
  const handleContinueToDiagnosis = useCallback(
    (options = {}) => {
      setCurrentStep(STEPS.DIAGNOSIS);
      setVisitedSteps(
        (prev) =>
          new Set([...prev, eventsNote ? STEPS.EVENTS : STEPS.RECORD, STEPS.DIAGNOSIS]),
      );
      // Events note type has no Narrative: run the differential from the event text now.
      if (eventsNote) {
        const source = Array.isArray(options.events)
          ? buildEventsSourceText(options.events)
          : eventsSourceText;
        if (source && source.length >= 50) {
          analysisTriggeredRef.current = true;
          runDdx(source);
        }
      }
      autoSaveSessionRef.current?.("handleContinueToDiagnosis");
    },
    [eventsNote, eventsSourceText, runDdx],
  );

  /**
   * Events note type: Add Event finishes the Event step and advances to Diagnosis.
   */
  const handleAddEventAndContinueToDiagnosis = useCallback(
    (name, comment = "") => {
      if (!String(name || "").trim()) return;
      const event = createInpatientEvent(name, comment);
      const nextEvents = [...inpatientEvents, event];
      setInpatientEvents(nextEvents);
      handleContinueToDiagnosis({ events: nextEvents });
    },
    [inpatientEvents, handleContinueToDiagnosis],
  );

  /**
   * Continue to Diagnostic Plan step
   */
  // ============================================================================
  // PEARLS (folded in from ChartMind V2)
  // ============================================================================

  // Teaching pearls: first chosen diagnosis normally; Events uses the full working differential
  // (every diagnosis not hidden). `pearlsUnavailable` outlives a cleared session on purpose: it
  // means the prompt is not set up on this environment, not anything about the encounter.
  const [pearls, setPearls] = useState([]);
  const [pearlsSubject, setPearlsSubject] = useState("");
  const [pearlsLoading, setPearlsLoading] = useState(false);
  const [pearlsError, setPearlsError] = useState("");
  const [pearlsUnavailable, setPearlsUnavailable] = useState(false);
  // Bumped by every run and by clearSession, so a late answer for an older diagnosis or a
  // previous encounter is dropped instead of landing in this one.
  const pearlsRequestRef = useRef(0);

  const runPearls = useCallback(
    async (subject) => {
      if (!subject) return;
      const requestId = ++pearlsRequestRef.current;
      setPearlsLoading(true);
      setPearlsError("");
      try {
        const result = await generatePearls({
          subject,
          organizationId,
          patientContext: patientContextPayload,
          ...(eventsNote
            ? {
                ...eventsPearlsPromptOptions(specialty),
                clinicalContext: eventsSourceText,
              }
            : promptOptions(PEARLS_PROMPT_ID, encounterSetting, specialty)),
        });
        if (requestId !== pearlsRequestRef.current) return;
        setPearls(result);
        setPearlsSubject(subject);
        onDataChangedRef.current?.();
      } catch (err) {
        if (requestId !== pearlsRequestRef.current) return;
        // The page moves a clinician already on the step on through its normal Next, so the
        // step after it generates as usual.
        if (isMissingPromptError(err)) setPearlsUnavailable(true);
        else setPearlsError(err?.message || "Could not load teaching pearls.");
      } finally {
        if (requestId === pearlsRequestRef.current) setPearlsLoading(false);
      }
    },
    [
      organizationId,
      patientContextPayload,
      encounterSetting,
      specialty,
      eventsNote,
      eventsSourceText,
    ],
  );

  const diagnosisNamesForPearls = selectedDiagnoses
    .map((d) => d.condition || d.name)
    .filter(Boolean);
  // Events: all kept diagnoses. Other note types: primary selection only.
  // Events: one diagnosis per line so the model can return pearls grouped by diagnosis.
  const pearlsTarget = eventsNote
    ? diagnosisNamesForPearls.join("\n")
    : diagnosisNamesForPearls[0] || "";

  // Generate on arrival, however the step was reached (Next, the stepper, Back), and again when
  // the differential (or chosen diagnosis) has changed since the pearls on screen were written.
  useEffect(() => {
    if (currentStep !== STEPS.PEARLS || pearlsUnavailable || pearlsLoading || pearlsError) return;
    if (pearlsTarget && pearlsTarget !== pearlsSubject) void runPearls(pearlsTarget);
  }, [currentStep, pearlsTarget, pearlsSubject, pearlsUnavailable, pearlsLoading, pearlsError, runPearls]);

  const handleContinueToExam = useCallback(() => {
    setCurrentStep(STEPS.EXAM);
    setVisitedSteps((prev) => new Set([...prev, STEPS.DIAGNOSIS, STEPS.EXAM]));
    autoSaveSessionRef.current?.("handleContinueToExam");
  }, []);

  // Exam (and Diagnostics) share one chartmind-plan call. Fire whenever Exam is shown —
  // Next, stepper jump, or Back — same pattern as Pearls. Cache in the hook avoids dupes.
  useEffect(() => {
    if (currentStep !== STEPS.EXAM) return;
    if (planSourceText && selectedDiagnoses.length > 0) {
      diagnosticPlanHook.generatePlan(
        planSourceText,
        selectedDiagnoses,
        patientContextPayload,
      );
    }
  }, [
    currentStep,
    planSourceText,
    selectedDiagnoses,
    diagnosticPlanHook.generatePlan,
    patientContextPayload,
  ]);

  const handleContinueToPearls = useCallback(() => {
    setCurrentStep(STEPS.PEARLS);
    setVisitedSteps((prev) => new Set([...prev, STEPS.DIAGNOSIS, STEPS.EXAM, STEPS.PEARLS]));
    autoSaveSessionRef.current?.("handleContinueToPearls");
  }, []);

  const handleContinueToPlan = useCallback(() => {
    setCurrentStep(STEPS.DIAGNOSTIC_PLAN);
    setVisitedSteps(
      (prev) =>
        new Set([
          ...prev,
          STEPS.DIAGNOSIS,
          STEPS.EXAM,
          ...(pearlsUnavailable ? [] : [STEPS.PEARLS]),
          STEPS.DIAGNOSTIC_PLAN,
        ]),
    );
    // Reuse cached exam+tests from Exam; regenerate only if cache miss / inputs changed.
    if (planSourceText && selectedDiagnoses.length > 0) {
      diagnosticPlanHook.generatePlan(
        planSourceText,
        selectedDiagnoses,
        patientContextPayload,
      );
    }
    autoSaveSessionRef.current?.("handleContinueToPlan");
  }, [
    pearlsUnavailable,
    planSourceText,
    selectedDiagnoses,
    diagnosticPlanHook.generatePlan,
    patientContextPayload,
  ]);

  /**
   * Continue to Confirm Diagnosis step
   */
  const handleContinueToConfirm = useCallback(() => {
    setCurrentStep(STEPS.CONFIRM_DIAGNOSIS);
    setVisitedSteps(prev => new Set([...prev, STEPS.DIAGNOSTIC_PLAN, STEPS.CONFIRM_DIAGNOSIS]));
    // Generate confirmed diagnoses based on test results
    // IMPORTANT: Pass the ORIGINAL DDX data (not just selected diagnoses) as the seed
    if (recordingHook.transcript && diagnosisHook.ddxData) {
      confirmDiagnosisHook.generateConfirmedDiagnoses(
        recordingHook.transcript,
        testResults,
        diagnosisHook.ddxData,  // Original DDX is the seed
        disabledDiagnosisIds,   // Pass disabled diagnosis IDs to LLM
        patientContextPayload
      );
    }
    autoSaveSessionRef.current?.('handleContinueToConfirm');
  }, [
    recordingHook.transcript,
    diagnosisHook.ddxData,
    testResults,
    disabledDiagnosisIds,
    confirmDiagnosisHook.generateConfirmedDiagnoses,
    patientContextPayload
  ]);

  /**
   * Continue to Treatment step (Daily Plan on admission/progress; Treatments on Events).
   * Exam + Diagnostics already ran earlier — only generate treatments here.
   */
  const handleContinueToTreatment = useCallback(() => {
    setCurrentStep(STEPS.TREATMENT);
    setVisitedSteps((prev) =>
      new Set([
        ...prev,
        STEPS.DIAGNOSIS,
        STEPS.EXAM,
        ...(pearlsUnavailable ? [] : [STEPS.PEARLS]),
        STEPS.DIAGNOSTIC_PLAN,
        STEPS.TREATMENT,
      ]),
    );
    // Generate treatment plan - use confirmed diagnoses if available, otherwise use preliminary
    const diagnosesForTreatment = selectedConfirmedDiagnoses.length > 0
      ? selectedConfirmedDiagnoses
      : selectedDiagnoses;

    if (planSourceText && diagnosesForTreatment.length > 0) {
      treatmentPlanHook.generatePlan(
        planSourceText,
        diagnosesForTreatment,
        diagnosticPlanHook.planData,
        patientContextPayload,
      );
    }
    autoSaveSessionRef.current?.("handleContinueToTreatment");
  }, [
    pearlsUnavailable,
    planSourceText,
    selectedDiagnoses,
    selectedConfirmedDiagnoses,
    diagnosticPlanHook.planData,
    treatmentPlanHook.generatePlan,
    patientContextPayload,
  ]);

  /**
   * Continue to Events step (inpatient only)
   */
  const handleContinueToEvents = useCallback(() => {
    setCurrentStep(STEPS.EVENTS);
    setVisitedSteps((prev) => new Set([...prev, STEPS.TREATMENT, STEPS.EVENTS]));
    autoSaveSessionRef.current?.("handleContinueToEvents");
  }, []);

  /**
   * Continue to Discharge step
   */
  const handleContinueToDischarge = useCallback(() => {
    setCurrentStep(STEPS.DISCHARGE);
    setVisitedSteps(prev => new Set([...prev, STEPS.TREATMENT, STEPS.DISCHARGE]));
    autoSaveSessionRef.current?.('handleContinueToDischarge');
  }, []);

  /**
   * Generate the note unless one already exists for this transcript + diagnosis combo.
   * Shared by the normal Discharge -> Chart hand-off and the Record -> Chart skip.
   */
  const generateNoteIfStale = useCallback(async () => {
    const genKey = getGenerationKey(clinicalSourceText, selectedDiagnoses);
    const alreadyGenerated =
      chartHook.hasNote &&
      chartHook.noteSections &&
      Object.keys(chartHook.noteSections).length > 0 &&
      generatedForRef.current === genKey;

    if (!alreadyGenerated) {
      generatedForRef.current = genKey;
      await chartHook.generateNote(
        clinicalSourceText,
        selectedDiagnoses,
        patientContextPayload,
        noteEncounterDetails,
      );
    }
  }, [
    clinicalSourceText,
    selectedDiagnoses,
    chartHook.generateNote,
    chartHook.hasNote,
    chartHook.noteSections,
    getGenerationKey,
    patientContextPayload,
    inpatientNoteContext,
    noteEncounterDetails,
  ]);

  // Key describing the inputs the note SHOULD reflect right now. Computed during render from the
  // same function the generator uses, so the staleness banner can never disagree with the caching
  // decision in generateNoteIfStale.
  const currentGenerationKey = getGenerationKey(
    clinicalSourceText || "",
    selectedDiagnoses,
  );

  /**
   * True when a note is on screen but the transcript or diagnoses have changed since it was
   * generated — the "go back, add detail, return to Chart" case. Drives an inline prompt to
   * regenerate rather than silently showing an out-of-date note.
   */
  const isNoteStale = Boolean(
    chartHook.hasNote &&
      generatedForRef.current &&
      generatedForRef.current !== currentGenerationKey,
  );

  /**
   * Generate on arrival at Chart when there is nothing to show yet.
   *
   * The stepper navigates with setCurrentStep directly, bypassing the navigation handlers, so
   * without this a user who jumps to Chart lands on an empty step and has to press a button for
   * something we already have every input to produce. Keyed by generation key so a failed or
   * empty result cannot loop.
   */
  const autoGeneratedForRef = useRef(null);
  useEffect(() => {
    if (currentStep !== STEPS.CHART) return;
    if (chartHook.loading || chartHook.error || chartHook.hasNote) return;
    if (!clinicalSourceText || clinicalSourceText.trim().length < 50) return;
    if (autoGeneratedForRef.current === currentGenerationKey) return;
    autoGeneratedForRef.current = currentGenerationKey;
    void generateNoteIfStale();
  }, [
    currentStep,
    chartHook.loading,
    chartHook.error,
    chartHook.hasNote,
    clinicalSourceText,
    currentGenerationKey,
    generateNoteIfStale,
  ]);

  /**
   * Continue to Chart step
   */
  const handleContinueToChart = useCallback(async () => {
    setCurrentStep(STEPS.CHART);
    setVisitedSteps((prev) =>
      new Set([...prev, eventsNote ? STEPS.TREATMENT : STEPS.DISCHARGE, STEPS.CHART]),
    );
    await generateNoteIfStale();
    autoSaveSessionRef.current?.("handleContinueToChart");
  }, [generateNoteIfStale, eventsNote]);

  /**
   * Skip Record -> Chart, generating the note from the transcript alone.
   *
   * Deliberately NOT handleContinueToChart: that marks Discharge visited as well, which would
   * unlock a step the user just chose to skip and let it auto-generate against no diagnoses.
   * Only Chart is marked visited here, so the stepper still reflects what was actually done.
   */
  const handleSkipToChart = useCallback(async () => {
    setCurrentStep(STEPS.CHART);
    setVisitedSteps(prev => new Set([...prev, STEPS.CHART]));
    await generateNoteIfStale();
    autoSaveSessionRef.current?.('handleSkipToChart');
  }, [generateNoteIfStale]);

  /**
   * Copy note to clipboard
   */
  const handleCopyToClipboard = useCallback(async () => {
    const text = chartHook.formatForClipboard();
    await navigator.clipboard.writeText(text);
  }, [chartHook.formatForClipboard]);

  /**
   * Export clinical note to PDF
   */
  const handleExportToPdf = useCallback(async () => {
    if (!chartHook.noteSections || !chartHook.template) {
      throw new Error("No clinical note available to export.");
    }

    const { fileName, blob } = await exportClinicalNotePdf({
      templateName: chartHook.template.name,
      sections: chartHook.template.sections.map(({ key, title }) => ({
        key,
        title,
      })),
      noteSections: chartHook.noteSections,
    });

    downloadBlob(blob, fileName);
  }, [chartHook.noteSections, chartHook.template]);

  /**
   * Regenerate note
   */
  const handleRegenerate = useCallback(async () => {
    generatedForRef.current = null;
    const genKey = getGenerationKey(clinicalSourceText, selectedDiagnoses);
    generatedForRef.current = genKey;
    await chartHook.generateNote(
      clinicalSourceText,
      selectedDiagnoses,
      patientContextPayload,
      noteEncounterDetails,
    );
  }, [
    clinicalSourceText,
    selectedDiagnoses,
    chartHook.generateNote,
    getGenerationKey,
    patientContextPayload,
    noteEncounterDetails,
  ]);

  /**
   * Handle back navigation along the Exam → Pearls → Diagnostics → Treatments trio.
   */
  const handleBack = useCallback(() => {
    const shortFlow = inpatient || eventsNote;
    if (eventsNote) {
      if (currentStep === STEPS.DIAGNOSIS) {
        setCurrentStep(STEPS.EVENTS);
      } else if (currentStep === STEPS.EXAM) {
        setCurrentStep(STEPS.DIAGNOSIS);
      } else if (currentStep === STEPS.PEARLS) {
        setCurrentStep(STEPS.EXAM);
      } else if (currentStep === STEPS.DIAGNOSTIC_PLAN) {
        setCurrentStep(pearlsUnavailable ? STEPS.EXAM : STEPS.PEARLS);
      } else if (currentStep === STEPS.TREATMENT) {
        setCurrentStep(STEPS.DIAGNOSTIC_PLAN);
      } else if (currentStep === STEPS.CHART) {
        setCurrentStep(STEPS.TREATMENT);
      }
      autoSaveSessionRef.current?.("handleBack");
      return;
    }

    if (currentStep === STEPS.DIAGNOSIS) {
      setCurrentStep(STEPS.RECORD);
    } else if (currentStep === STEPS.EXAM) {
      setCurrentStep(STEPS.DIAGNOSIS);
    } else if (currentStep === STEPS.PEARLS) {
      setCurrentStep(STEPS.EXAM);
    } else if (currentStep === STEPS.DIAGNOSTIC_PLAN) {
      setCurrentStep(pearlsUnavailable ? STEPS.EXAM : STEPS.PEARLS);
    } else if (currentStep === STEPS.CONFIRM_DIAGNOSIS) {
      setCurrentStep(STEPS.DIAGNOSTIC_PLAN);
    } else if (currentStep === STEPS.TREATMENT) {
      // Full outpatient: Confirm. CM0427 / inpatient: Diagnostics (Confirm is hidden).
      setCurrentStep(shortFlow ? STEPS.DIAGNOSTIC_PLAN : STEPS.CONFIRM_DIAGNOSIS);
    } else if (currentStep === STEPS.EVENTS) {
      // Legacy mid-flow Events (Admission/Progress) — back goes to Daily Plan.
      setCurrentStep(STEPS.TREATMENT);
    } else if (currentStep === STEPS.DISCHARGE) {
      setCurrentStep(STEPS.TREATMENT);
    } else if (currentStep === STEPS.CHART) {
      setCurrentStep(STEPS.DISCHARGE);
    }
    autoSaveSessionRef.current?.("handleBack");
  }, [currentStep, inpatient, eventsNote, pearlsUnavailable]);

  // ============================================================================
  // SERIALIZATION (Phase 2) - MUST BE BEFORE AUTO-SAVE
  // ============================================================================

  /**
   * Helper: Check if a value is "empty" (null, undefined, empty string, empty array, empty object)
   */
  const isEmpty = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'object' && Object.keys(value).length === 0) return true;
    return false;
  };

  /**
   * Helper: Remove empty values from an object recursively
   */
  const removeEmptyValues = (obj) => {
    if (Array.isArray(obj)) {
      const filtered = obj.filter(item => !isEmpty(item));
      return filtered.length > 0 ? filtered : undefined;
    }
    
    if (typeof obj === 'object' && obj !== null) {
      const cleaned = {};
      for (const [key, value] of Object.entries(obj)) {
        const cleanedValue = removeEmptyValues(value);
        if (!isEmpty(cleanedValue)) {
          cleaned[key] = cleanedValue;
        }
      }
      return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    }
    
    return isEmpty(obj) ? undefined : obj;
  };

  /**
   * Serialize current session state to JSON-safe object for Firestore
   * Optimizations:
   * - Excludes: empty sections, navigation, template, version
   * - Excludes: timestamp (added by Firestore via serverTimestamp)
   * - Stores diagnosis references (condition names) instead of full objects
   * - Minimal data for efficient storage
   */
  const serializeSession = useCallback(() => {
    // Source documents (clinical references + embedded RAG docs) backing a
    // step's LLM response — compacted to display fields so RAG chunk text
    // never lands in Firestore. Omitted entirely when empty.
    const sourceFields = (hook) => {
      const clinicalReferences = compactSourceList(hook.clinicalReferences);
      const embeddedDocuments = compactSourceList(hook.embeddedDocuments);
      return {
        ...(clinicalReferences.length > 0 && { clinicalReferences }),
        ...(embeddedDocuments.length > 0 && { embeddedDocuments }),
      };
    };

    const raw = {
      // Navigation state (current step)
      navigation: {
        currentStep,
        encounterStarted,
      },

      pearls: pearls.length ? { pearls, subject: pearlsSubject } : undefined,

      specialty,

      // Only inpatient is stored; a session without it is outpatient.
      encounterSetting: inpatient ? encounterSetting : undefined,
      inpatient: inpatient ? {
        noteFormat,
        events: inpatientEvents,
        dailyPlanComments,
        dailyPlanReassuring,
      } : undefined,
      
      // Recording state (only if transcript exists)
      recording: recordingHook.transcript && recordingHook.transcript.trim() ? {
        transcript: recordingHook.transcript,
      } : undefined,
      
      // Diagnosis state (only if has data)
      // Store ddxData (full AI response) + selected diagnosis names (not full objects)
      diagnosis: (diagnosisHook.ddxData || (selectedDiagnoses && selectedDiagnoses.length > 0) || disabledDiagnosisIds.size > 0 || customDiagnoses.length > 0 || clinicianImpression) ? {
        ...(diagnosisHook.ddxData && { ddxData: diagnosisHook.ddxData }),
        ...(customDiagnoses.length > 0 && { customDiagnoses }),
        // The pre-differential answer. Saved so reopening a chart does not re-ask a question
        // that can only honestly be answered once.
        ...(clinicianImpression && { clinicianImpression }),
        // Store only condition names, not full objects (reduces duplication)
        ...(selectedDiagnoses && selectedDiagnoses.length > 0 && { 
          selectedDiagnosisNames: selectedDiagnoses.map(d => d.condition || d.name)
        }),
        ...(disabledDiagnosisIds.size > 0 && { disabledDiagnosisIds: Array.from(disabledDiagnosisIds) }),
        ...sourceFields(diagnosisHook),
      } : undefined,
      
      // Diagnostic plan state (only if has data)
      diagnosticPlan: (() => {
        const exam2Dirty = exam2Systems.some((row) => {
          const values = Array.isArray(row.value)
            ? row.value.map((item) => String(item || "").trim()).filter(Boolean)
            : String(row.value || "").trim()
              ? [String(row.value).trim()]
              : [];
          return values.length > 0 && !(row.id === "general" && examMode === "exam");
        });
        const hasExam2State = examMode === "exam2" || exam2Dirty;
        const plan2Dirty = hasPlan2State(plan2ByCategory);
        if (
          !(
            diagnosticPlanHook.planData ||
            disabledTestIds.size > 0 ||
            disabledExamIds.size > 0 ||
            examResults.size > 0 ||
            testResults.size > 0 ||
            hasExam2State ||
            plan2Dirty
          )
        ) {
          return undefined;
        }
        return {
          ...(diagnosticPlanHook.planData && { planData: diagnosticPlanHook.planData }),
          ...(disabledTestIds.size > 0 && { disabledTestIds: Array.from(disabledTestIds) }),
          ...(disabledExamIds.size > 0 && { disabledExamIds: Array.from(disabledExamIds) }),
          ...(examResults.size > 0 && { examResults: Object.fromEntries(examResults) }),
          // Always persist mode so list vs Exam 2 / *2 survives reload (default is *2 when absent).
          examMode: examMode === "exam2" ? "exam2" : "exam",
          ...(hasExam2State && { exam2Systems }),
          // Convert Map to plain object for Firestore (doesn't support nested arrays)
          ...(testResults.size > 0 && { testResults: Object.fromEntries(testResults) }),
          plan2ByCategory,
        };
      })(),
      
      // Confirmed diagnosis state (only if has data)
      confirmDiagnosis: (confirmDiagnosisHook.confirmedData || (selectedConfirmedDiagnoses && selectedConfirmedDiagnoses.length > 0)) ? {
          ...(confirmDiagnosisHook.confirmedData && { confirmedData: confirmDiagnosisHook.confirmedData }),
          // Store only condition names, not full objects (reduces duplication)
          ...(selectedConfirmedDiagnoses && selectedConfirmedDiagnoses.length > 0 && { 
            selectedConfirmedDiagnosisNames: selectedConfirmedDiagnoses.map(d => d.condition || d.name)
          }),
        } : undefined,
      
      // Treatment state (only if has data)
      treatment: (() => {
        const treatment2Dirty = hasPlan2State(treatment2ByCategory);
        if (
          !(
            treatmentPlanHook.planData ||
            disabledTreatmentIds.size > 0 ||
            selectedTreatments.size > 0 ||
            customTreatments.length > 0 ||
            treatment2Dirty
          )
        ) {
          return undefined;
        }
        return {
          ...(treatmentPlanHook.planData && { planData: treatmentPlanHook.planData }),
          ...(customTreatments.length > 0 && { customTreatments }),
          ...(disabledTreatmentIds.size > 0 && { disabledTreatmentIds: Array.from(disabledTreatmentIds) }),
          ...(selectedTreatments.size > 0 && { selectedTreatments: Array.from(selectedTreatments) }),
          treatment2ByCategory,
          ...sourceFields(treatmentPlanHook),
        };
      })(),
      
      // Discharge state (only if has instructions or patientName - date only included if other data exists)
      discharge: (() => {
        const hasInstructions = dischargeHook.instructions && dischargeHook.instructions.length > 0;
        const hasPatientName = dischargeHook.patientName && dischargeHook.patientName.trim();
        
        // Only create discharge object if there are instructions or patientName
        if (!hasInstructions && !hasPatientName) {
          return undefined;
        }
        
        // Build discharge object with only non-empty fields
        const discharge = {};
        if (hasInstructions) {
          discharge.instructions = dischargeHook.instructions;
          discharge.includedInstructionIds = Array.from(dischargeHook.includedInstructionIds);
        }
        if (hasPatientName) {
          discharge.patientName = dischargeHook.patientName;
        }
        // Only include date if there are instructions or patientName (not just default date alone)
        if ((hasInstructions || hasPatientName) && dischargeHook.date) {
          discharge.date = dischargeHook.date;
        }
        if (dischargeHook.hasBeenGenerated) {
          discharge.hasBeenGenerated = dischargeHook.hasBeenGenerated;
        }
        Object.assign(discharge, sourceFields(dischargeHook));

        return Object.keys(discharge).length > 0 ? discharge : undefined;
      })(),
      
      // Imported patient context (when a clinician imports a shared record).
      //
      // Identity + consent always: that is what restores the "Send Discharge Instructions"
      // path on reload. The clinical record itself is gated on
      // PERSIST_IMPORTED_PATIENT_RECORD (see the constant at the top of this file) because
      // storing it outlives the share token's 60-minute consent window. Without it, a
      // reloaded session has a patient but no record, which is what
      // patientContext.recordUnavailable warns the clinician about.
      patientContext: (patientContext && patientContext.patientUserId) ? {
        patientUserId: patientContext.patientUserId,
        ...(patientContext.patientName && { patientName: patientContext.patientName }),
        ...(patientContext.patientDob && { patientDob: patientContext.patientDob }),
        ...(Array.isArray(patientContext.allowedSections) && patientContext.allowedSections.length > 0 && {
          allowedSections: patientContext.allowedSections,
        }),
        ...(PERSIST_IMPORTED_PATIENT_RECORD && patientContext.record && {
          record: patientContext.record,
        }),
        ...(PERSIST_IMPORTED_PATIENT_RECORD && patientContext.careStartResult && {
          careStartResult: patientContext.careStartResult,
        }),
        // Gated with the other two, not forgotten: the VisitStart check-in is the
        // patient's self-report and belongs to the same consent window. Left out,
        // flipping the flag above would restore the record and the CareStart result
        // after a reload and silently drop the intake.
        ...(PERSIST_IMPORTED_PATIENT_RECORD && patientContext.visitStartIntake && {
          visitStartIntake: patientContext.visitStartIntake,
        }),
      } : undefined,

      // Chart state (persist note sections and user feedback together)
      chart:
        (chartHook.noteSections &&
          Object.keys(chartHook.noteSections).length > 0) ||
        chartHook.feedback
          ? {
              ...(chartHook.noteSections && {
                noteSections: chartHook.noteSections,
              }),
              ...(chartHook.feedback && {
                feedback: chartHook.feedback,
              }),
              ...sourceFields(chartHook),
            }
          : undefined,
    };

    // Remove all undefined/empty values recursively
    return removeEmptyValues(raw);
  }, [
    currentStep,
    encounterStarted,
    recordingHook.transcript,
    diagnosisHook.ddxData,
    diagnosisHook.clinicalReferences,
    diagnosisHook.embeddedDocuments,
    selectedDiagnoses,
    customDiagnoses,
    clinicianImpression,
    disabledDiagnosisIds,
    diagnosticPlanHook.planData,
    disabledTestIds,
    disabledExamIds,
    examResults,
    examMode,
    exam2Systems,
    testResults,
    plan2ByCategory,
    confirmDiagnosisHook.confirmedData,
    selectedConfirmedDiagnoses,
    treatmentPlanHook.planData,
    treatmentPlanHook.clinicalReferences,
    treatmentPlanHook.embeddedDocuments,
    customTreatments,
    disabledTreatmentIds,
    selectedTreatments,
    treatment2ByCategory,
    dischargeHook.instructions,
    dischargeHook.includedInstructionIds,
    dischargeHook.patientName,
    dischargeHook.date,
    dischargeHook.hasBeenGenerated,
    dischargeHook.clinicalReferences,
    dischargeHook.embeddedDocuments,
    chartHook.noteSections,
    chartHook.feedback,
    chartHook.clinicalReferences,
    chartHook.embeddedDocuments,
    patientContext,
    inpatient,
    encounterSetting,
    noteFormat,
    specialty,
    inpatientEvents,
    dailyPlanComments,
    dailyPlanReassuring,
    pearls,
    pearlsSubject,
  ]);

  // ============================================================================
  // AUTO-SAVE LOGIC
  // ============================================================================

  /**
   * Check if session data has changed since last save
   * Uses JSON.stringify for deep comparison (safe because data is JSON-serializable)
   */
  const hasDataChanged = useCallback((currentData) => {
    if (!lastSavedSnapshotRef.current) return true;
    
    const currentJson = JSON.stringify(currentData);
    const lastSavedJson = JSON.stringify(lastSavedSnapshotRef.current);
    
    return currentJson !== lastSavedJson;
  }, []);

  /**
   * Auto-save session to Firestore (only if data changed)
   * This is called from three places:
   * 1. Immediately after AI workflow completes
   * 2. After 3s debounce when user edits data
   * 3. When navigating between steps
   * 
   * Guards against concurrent saves to prevent race conditions
   * 
   * @param {string} source - Debug label for where this save was triggered from
   */
  const autoSaveSession = useCallback(async (source = 'unknown') => {
    // [BETA-KIJABE] Noop all saves for Kijabe beta testers — remove when beta ends
    if (disableSave) return;

    if (!user?.uid) return;
    
    // CRITICAL: Prevent auto-save during hydration (blocks all paths: AI callbacks + debounced edits)
    if (isHydratingRef.current) return;
    
    // CRITICAL: Prevent concurrent saves (race condition guard)
    if (isSavingRef.current) return;
    
    // CRITICAL: Don't save blank encounters - require at least a transcript
    if (!recordingHook.transcript || recordingHook.transcript.trim().length === 0) return;
    
    const currentData = serializeSession();
    
    // Skip if no meaningful data yet (empty object)
    if (!currentData || Object.keys(currentData).length === 0) return;
    
    // Skip if data unchanged
    if (!hasDataChanged(currentData)) return;
    
    try {
      // Set lock BEFORE any async operations
      isSavingRef.current = true;
      setSaving(true);
      
      // Create session ID if this is first save
      const isFirstSave = !sessionId;
      const id =
        sessionId ||
        (await sessionService.createNewSession(user.uid, reportingMetadata));
      
      // Generate title on first save (background - non-blocking)
      if (isFirstSave && recordingHook.transcript) {
        sessionService.generateSessionTitle(id, recordingHook.transcript)
          .catch(err => console.warn('[AutoSave] Title generation failed:', err.message));
      }
      
      // Save to Firestore
      await sessionService.saveSession(
        id,
        currentData,
        user.uid,
        reportingMetadata,
      );
      
      // Set as active session if new
      if (isFirstSave) {
        setSessionId(id);
        await sessionService.setActiveSessionId(user.uid, id);
      }
      
      // Update snapshot and clear unsaved flag
      lastSavedSnapshotRef.current = currentData;
      setHasUnsavedChanges(false);
      setLastSavedAt(new Date());
      
      return id;
    } catch (error) {
      console.error('[AutoSave] Save failed:', error);
      setSessionError(error.message);
    } finally {
      // Release lock AFTER everything completes
      isSavingRef.current = false;
      setSaving(false);
    }
  }, [
    user?.uid,
    sessionId,
    serializeSession,
    hasDataChanged,
    recordingHook.transcript,
    reportingMetadata,
  ]);

  // Create debounced version for navigation saves (3s delay to catch accidental clicks)
  const debouncedAutoSaveNavigation = useDebouncedCallback(
    (source) => autoSaveSession(source),
    3000,
    { leading: false, trailing: true }
  );

  // Wire up the refs to autoSaveSession after it's defined
  useEffect(() => {
    onDataChangedRef.current = () => autoSaveSession('ai-callback');
    autoSaveSessionRef.current = (source) => debouncedAutoSaveNavigation(source || 'ref');
  }, [autoSaveSession, debouncedAutoSaveNavigation]);

  // Keep serializeSessionRef current so loadSession's setTimeout can call the latest version
  useEffect(() => {
    serializeSessionRef.current = serializeSession;
  }, [serializeSession]);

  /**
   * Create a snapshot of current session for debounced save detection
   * This tracks all user-editable state
   */
  const currentSessionSnapshot = useMemo(() => 
    JSON.stringify(serializeSession())
  , [
    serializeSession,
    // Navigation state
    currentStep,
    // Recording state
    recordingHook.transcript,
    // Diagnosis state
    diagnosisHook.ddxData,
    selectedDiagnoses,
    customDiagnoses,
    clinicianImpression,
    // Diagnostic plan state
    diagnosticPlanHook.planData,
    disabledTestIds,
    disabledExamIds,
    examResults,
    examMode,
    exam2Systems,
    testResults,
    plan2ByCategory,
    // Confirmed diagnosis state
    confirmDiagnosisHook.confirmedData,
    selectedConfirmedDiagnoses,
    // Treatment state
    treatmentPlanHook.planData,
    customTreatments,
    disabledTreatmentIds,
    selectedTreatments,
    treatment2ByCategory,
    // Discharge state
    dischargeHook.instructions,
    dischargeHook.includedInstructionIds,
    dischargeHook.patientName,
    dischargeHook.date,
    dischargeHook.hasBeenGenerated,
    // Chart state
    chartHook.noteSections,
    chartHook.feedback,
    // Inpatient state
    encounterSetting,
    noteFormat,
    specialty,
    inpatientEvents,
    dailyPlanComments,
    dailyPlanReassuring,
    pearls,
  ]);

  // Debounce the snapshot for 3s (returns [value, { cancel }]) — only delays Firestore writes, not the dirty flag
  const [debouncedSnapshot, debouncedSnapshotControl] = useDebounce(currentSessionSnapshot, 3000);

  /**
   * Dirty flag: immediate (no debounce). Compare current serialized state to last successful save.
   */
  useEffect(() => {
    if (isHydratingRef.current) return;
    if (!recordingHook.transcript || recordingHook.transcript.trim().length === 0) {
      setHasUnsavedChanges(false);
      return;
    }
    const last = lastSavedSnapshotRef.current;
    const lastJson = last ? JSON.stringify(last) : null;
    if (!lastJson) {
      const data = serializeSession();
      const hasData = data && Object.keys(data).length > 0;
      setHasUnsavedChanges(!!hasData);
      return;
    }
    setHasUnsavedChanges(currentSessionSnapshot !== lastJson);
  }, [currentSessionSnapshot, recordingHook.transcript, serializeSession]);

  /**
   * Trigger auto-save when debounced snapshot changes (user edit detection)
   */
  useEffect(() => {
    if (!debouncedSnapshot) return;
    if (isHydratingRef.current) return; // Skip during hydration
    
    const lastSavedJson = lastSavedSnapshotRef.current 
      ? JSON.stringify(lastSavedSnapshotRef.current) 
      : null;
    
    if (debouncedSnapshot !== lastSavedJson) {
      autoSaveSessionRef.current?.('debounced-edit');
    }
  }, [debouncedSnapshot]);

  /**
   * Hydrate session state from serialized data
   * Restores state into all hooks and local state
   * Navigation is restored to the saved step (if valid), otherwise starts from the last step with data
   */
  const hydrateSession = useCallback((data) => {
    if (!data) return;

    try {
      // Restore navigation state if available
      const savedStep = data.navigation?.currentStep;
      const savedInpatient = isInpatient(data.encounterSetting);
      const savedNoteFormat = savedInpatient
        ? normalizeNoteFormat(data.inpatient?.noteFormat)
        : normalizeNoteFormat(null);
      const savedEventsNote = savedInpatient && isEventsNoteFormat(savedNoteFormat);

      // Determine which steps should be marked as visited based on saved data
      const stepsToVisit = new Set([
        savedEventsNote ? STEPS.EVENTS : STEPS.RECORD,
      ]);
      
      // Mark steps as visited based on what data exists
      if (data.diagnosis?.ddxData || data.diagnosis?.selectedDiagnosisNames) {
        stepsToVisit.add(STEPS.DIAGNOSIS);
      }
      if (data.diagnosticPlan?.planData) {
        stepsToVisit.add(STEPS.EXAM);
        stepsToVisit.add(STEPS.DIAGNOSTIC_PLAN);
      }
      if (data.pearls?.pearls?.length) {
        stepsToVisit.add(STEPS.PEARLS);
      }
      if (data.confirmDiagnosis?.confirmedData || data.confirmDiagnosis?.selectedConfirmedDiagnosisNames) {
        stepsToVisit.add(STEPS.CONFIRM_DIAGNOSIS);
      }
      if (data.treatment?.planData) {
        stepsToVisit.add(STEPS.TREATMENT);
      }
      // Mid-flow Events is Events-note-only now. Old Admission/Progress sessions that sat on
      // Events still remap below; do not unlock that tab for non-Events note types.
      if (savedEventsNote) {
        stepsToVisit.add(STEPS.EVENTS);
      }
      if (
        !savedEventsNote &&
        (data.discharge?.instructions ||
          data.discharge?.patientName ||
          data.chart?.noteSections)
      ) {
        stepsToVisit.add(STEPS.DISCHARGE);
      }
      if (data.chart?.noteSections) {
        stepsToVisit.add(STEPS.CHART);
      }
      
      // Determine which step to start on
      let startingStep;
      if (savedStep && stepsToVisit.has(savedStep)) {
        // If we have a saved step and it's valid (has data), use it
        startingStep = savedStep;
      } else {
        // Otherwise, determine the furthest step with data (last accessible step)
        const stepOrder = savedEventsNote
          ? [
              STEPS.EVENTS,
              STEPS.DIAGNOSIS,
              STEPS.EXAM,
              STEPS.PEARLS,
              STEPS.DIAGNOSTIC_PLAN,
              STEPS.TREATMENT,
              STEPS.CHART,
            ]
          : [
              STEPS.RECORD,
              STEPS.DIAGNOSIS,
              STEPS.EXAM,
              STEPS.PEARLS,
              STEPS.DIAGNOSTIC_PLAN,
              STEPS.CONFIRM_DIAGNOSIS,
              STEPS.TREATMENT,
              STEPS.DISCHARGE,
              STEPS.CHART,
            ];
        startingStep = savedEventsNote ? STEPS.EVENTS : STEPS.RECORD;
        
        for (const step of stepOrder) {
          if (stepsToVisit.has(step)) {
            startingStep = step;
          }
        }
      }

      // Events note type never lands on Narrative, Discharge, or Confirm once started.
      if (savedEventsNote) {
        if (startingStep === STEPS.RECORD) startingStep = STEPS.EVENTS;
        if (startingStep === STEPS.DISCHARGE || startingStep === STEPS.CONFIRM_DIAGNOSIS) {
          startingStep = STEPS.TREATMENT;
        }
      } else if (startingStep === STEPS.EVENTS) {
        // Legacy Admission/Progress mid-flow Events tab — send to Discharge.
        startingStep = STEPS.DISCHARGE;
        stepsToVisit.add(STEPS.DISCHARGE);
        stepsToVisit.delete(STEPS.EVENTS);
      }

      const savedStarted =
        data.navigation?.encounterStarted === true ||
        Boolean(data.recording?.transcript) ||
        normalizeInpatientEvents(data.inpatient?.events).length > 0 ||
        Boolean(data.diagnosis?.ddxData || data.diagnosis?.selectedDiagnosisNames) ||
        Boolean(data.chart?.noteSections);
      setEncounterStarted(savedStarted);
      
      // Start from the determined step
      setCurrentStep(startingStep);
      setVisitedSteps(stepsToVisit);

      // The session's own setting, without touching the user's default for new encounters.
      setEncounterSettingState(normalizeEncounterSetting(data.encounterSetting));
      setSpecialtyState(data.specialty ? normalizeSpecialty(data.specialty) : readDefaultSpecialty());
      setNoteFormatState(
        savedInpatient ? savedNoteFormat : readDefaultNoteFormat(),
      );
      setInpatientEvents(normalizeInpatientEvents(data.inpatient?.events));
      setDailyPlanComments(data.inpatient?.dailyPlanComments || {});
      setDailyPlanReassuring(data.inpatient?.dailyPlanReassuring || {});
      setPearls(data.pearls?.pearls || []);
      setPearlsSubject(data.pearls?.subject || "");

      // Restore recording (transcript only - can't restore MediaRecorder state)
      if (data.recording?.transcript) {
        recordingHook.updateTranscript(data.recording.transcript);
      }

      // Restore imported patient context. `record` / `careStartResult` are present only if
      // the session was saved with PERSIST_IMPORTED_PATIENT_RECORD enabled; spreading the
      // stored object restores them when they are there and leaves identity-only otherwise.
      // Identity-only sets patientContext.recordUnavailable, which prompts a re-import
      // instead of letting the steps run without the record.
      if (data.patientContext?.patientUserId) {
        setPatientContext(data.patientContext);
      }

      // Restore diagnosis
      diagnosisHook.hydrateSourceDocuments(data.diagnosis);
      if (data.diagnosis) {
        // Restore ddxData into diagnosis hook
        if (data.diagnosis.ddxData) {
          diagnosisHook.setDdxData(data.diagnosis.ddxData);
        }

        const customDx = data.diagnosis.customDiagnoses || [];
        setCustomDiagnoses(customDx);

        if (data.diagnosis.clinicianImpression) {
          setClinicianImpression(data.diagnosis.clinicianImpression);
        }
        
        // Reconstruct selectedDiagnoses from names (include user-added diagnoses in lookup)
        if (data.diagnosis.selectedDiagnosisNames) {
          const allDiagnoses = [
            ...(data.diagnosis.ddxData?.primary_diagnoses || []),
            ...(data.diagnosis.ddxData?.alternative_diagnoses || []),
            ...customDx,
          ];
          const reconstructed = data.diagnosis.selectedDiagnosisNames
            .map(name => allDiagnoses.find(d => (d.condition || d.name) === name))
            .filter(Boolean); // Remove any that weren't found
          setSelectedDiagnoses(reconstructed);
        }
        // Fallback for old format (full objects stored)
        else if (data.diagnosis.selectedDiagnoses) {
          setSelectedDiagnoses(data.diagnosis.selectedDiagnoses);
        }
        
        // Restore disabled diagnosis IDs
        if (data.diagnosis.disabledDiagnosisIds) {
          setDisabledDiagnosisIds(new Set(data.diagnosis.disabledDiagnosisIds));
        }
      }

      // Restore diagnostic plan
      if (data.diagnosticPlan) {
        // Restore plan data into hook
        if (data.diagnosticPlan.planData) {
          diagnosticPlanHook.setPlanData(data.diagnosticPlan.planData);
        }
        
        // Restore UI state
        if (data.diagnosticPlan.disabledTestIds) {
          setDisabledTestIds(new Set(data.diagnosticPlan.disabledTestIds));
        }
        if (data.diagnosticPlan.disabledExamIds) {
          setDisabledExamIds(new Set(data.diagnosticPlan.disabledExamIds));
        }
        if (data.diagnosticPlan.examResults) {
          setExamResults(new Map(Object.entries(data.diagnosticPlan.examResults)));
        }
        if (data.diagnosticPlan.examMode === "exam2" || data.diagnosticPlan.examMode === "exam") {
          setExamMode(data.diagnosticPlan.examMode);
        } else {
          setExamMode(savedInpatient ? "exam2" : "exam");
        }
        if (Array.isArray(data.diagnosticPlan.exam2Systems) && data.diagnosticPlan.exam2Systems.length) {
          const merged = mergeExam2Systems({
            patientContext: data.patientContext || null,
            extras: data.diagnosticPlan.planData?.exam2Extras || [],
            previousSystems: data.diagnosticPlan.exam2Systems,
          });
          const hydrateScope = {
            specialty: data.specialty ? normalizeSpecialty(data.specialty) : specialty,
            setting: normalizeEncounterSetting(data.encounterSetting),
            inpatient: Boolean(savedInpatient),
            noteFormat: savedInpatient ? savedNoteFormat : noteFormat,
          };
          setExam2Systems(
            applyExam2LayoutPrefs(merged, loadExam2LayoutPrefs(user?.uid, hydrateScope)),
          );
        }
        if (data.diagnosticPlan.testResults) {
          // Handle both old format (array of entries) and new format (object)
          const testResultsData = Array.isArray(data.diagnosticPlan.testResults)
            ? data.diagnosticPlan.testResults // Old format: [[key, value], ...]
            : Object.entries(data.diagnosticPlan.testResults); // New format: {key: value, ...}
          setTestResults(new Map(testResultsData));
        }
        const leanMode = savedInpatient ? "table" : "list";
        setPlan2ByCategory(
          data.diagnosticPlan.plan2ByCategory
            ? normalizePlan2ByCategory(data.diagnosticPlan.plan2ByCategory)
            : createEmptyPlan2ByCategory(leanMode),
        );
      } else {
        setPlan2ByCategory(createEmptyPlan2ByCategory(savedInpatient ? "table" : "list"));
        setExamMode(savedInpatient ? "exam2" : "exam");
      }

      // Restore confirmed diagnosis
      if (data.confirmDiagnosis) {
        // Restore confirmedData into confirm diagnosis hook
        if (data.confirmDiagnosis.confirmedData) {
          confirmDiagnosisHook.setConfirmedData(data.confirmDiagnosis.confirmedData);
        }
        
        // Reconstruct selectedConfirmedDiagnoses from names by looking up in confirmedData
        // Include `diagnoses` — new schema from useConfirmDiagnosis (not only legacy primary/alternative arrays)
        if (data.confirmDiagnosis.selectedConfirmedDiagnosisNames && data.confirmDiagnosis.confirmedData) {
          const cd = data.confirmDiagnosis.confirmedData;
          const allConfirmedDiagnoses = [
            ...(cd.confirmed_diagnoses || []),
            ...(cd.alternative_diagnoses || []),
            ...(cd.diagnoses || []),
          ];
          const names = data.confirmDiagnosis.selectedConfirmedDiagnosisNames;
          let reconstructed = names
            .map(name => allConfirmedDiagnoses.find(d => (d.condition || d.name) === name))
            .filter(Boolean);
          // If nothing matched (name drift / schema quirks) but names were saved, keep gating state consistent
          if (reconstructed.length === 0 && names.length > 0) {
            reconstructed = names.map((name) => ({
              condition: name,
              likelihood: 'Likely',
              rationale: '',
            }));
          }
          setSelectedConfirmedDiagnoses(reconstructed);
        }
        // Fallback for old format (full objects stored)
        else if (data.confirmDiagnosis.selectedConfirmedDiagnoses) {
          setSelectedConfirmedDiagnoses(data.confirmDiagnosis.selectedConfirmedDiagnoses);
        }
      }

      // Restore treatment
      treatmentPlanHook.hydrateSourceDocuments(data.treatment);
      if (data.treatment) {
        // Restore plan data into hook
        if (data.treatment.planData) {
          treatmentPlanHook.setPlanData(data.treatment.planData);
        }
        
        // Restore UI state
        if (data.treatment.customTreatments) {
          setCustomTreatments(data.treatment.customTreatments);
        }
        if (data.treatment.disabledTreatmentIds) {
          setDisabledTreatmentIds(new Set(data.treatment.disabledTreatmentIds));
        }
        if (data.treatment.selectedTreatments) {
          setSelectedTreatments(new Set(data.treatment.selectedTreatments));
        }
        setTreatment2ByCategory(
          data.treatment.treatment2ByCategory
            ? normalizeTreatment2ByCategory(data.treatment.treatment2ByCategory)
            : createEmptyTreatment2ByCategory(savedInpatient ? "table" : "list"),
        );
      } else {
        setTreatment2ByCategory(
          createEmptyTreatment2ByCategory(savedInpatient ? "table" : "list"),
        );
      }

      // Restore discharge
      if (data.discharge) {
        dischargeHook.hydrateDischargeData(data.discharge);
      }

      // Restore chart
      chartHook.setNoteSections(data.chart?.noteSections || null);
      chartHook.setFeedback(data.chart?.feedback || null);
      chartHook.hydrateSourceDocuments(data.chart);
    } catch (err) {
      console.error('[useChartMindSession] Error hydrating session:', err);
      throw new Error('Failed to restore session data');
    }
  }, [
    recordingHook.updateTranscript,
    diagnosisHook.setDdxData,
    diagnosisHook.hydrateSourceDocuments,
    diagnosticPlanHook.setPlanData,
    confirmDiagnosisHook.setConfirmedData,
    treatmentPlanHook.setPlanData,
    treatmentPlanHook.hydrateSourceDocuments,
    dischargeHook.hydrateDischargeData,
    chartHook.setNoteSections,
    chartHook.setFeedback,
    chartHook.hydrateSourceDocuments,
    setCurrentStep,
    setVisitedSteps,
    setSelectedDiagnoses,
    setDisabledTestIds,
    setTestResults,
    setSelectedConfirmedDiagnoses,
    setDisabledTreatmentIds,
    setSelectedTreatments,
    setPatientContext,
  ]);

  // ============================================================================
  // SESSION PERSISTENCE (Phase 3+4)
  // ============================================================================

  /**
   * Save current session to Firestore
   */
  const saveSession = useCallback(async () => {
    // [BETA-KIJABE] Noop manual save for Kijabe beta testers — remove when beta ends
    if (disableSave) return;

    if (!user?.uid) {
      setSessionError('Must be logged in to save session');
      throw new Error('User not authenticated');
    }

    setSaving(true);
    setSessionError(null);

    try {
      const data = serializeSession();
      const id =
        sessionId ||
        (await sessionService.createNewSession(user.uid, reportingMetadata));
      
      await sessionService.saveSession(id, data, user.uid, reportingMetadata);
      await sessionService.setActiveSessionId(user.uid, id);
      
      setSessionId(id);
      return id;
    } catch (error) {
      console.error('[useChartMindSession] Error saving session:', error);
      setSessionError(error.message);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [user?.uid, sessionId, serializeSession, reportingMetadata]);

  /**
   * Load session from Firestore
   */
  const loadSession = useCallback(async (id) => {
    if (!id) {
      throw new Error('Session ID is required');
    }

    setLoading(true);
    setSessionError(null);

    try {
      const session = await sessionService.loadSession(id);
      
      if (!session) {
        throw new Error('Session not found');
      }

      // Note: isHydratingRef and cancel() are already set in the auto-load effect
      // This is a backup for manual loadSession() calls
      if (!isHydratingRef.current) {
        isHydratingRef.current = true;
        debouncedSnapshotControl.cancel();
      }
      
      hydrateSession(session.data);
      setSessionId(id);
      
      // Wait one render cycle for React to flush all batched state updates from hydrateSession,
      // then capture the hydrated state as the baseline. Use serializeSessionRef (not the closure
      // value of serializeSession) so we get the post-hydration snapshot, not the pre-hydration one.
      setTimeout(() => {
        const currentState = serializeSessionRef.current?.() ?? serializeSession();
        lastSavedSnapshotRef.current = currentState;
        setHasUnsavedChanges(false);
        isHydratingRef.current = false;
        // Catch up on any edits the user made while hydration was blocking saves
        autoSaveSessionRef.current?.('post-hydration');
      }, 300);
      
      return session;
    } catch (error) {
      console.error('[useChartMindSession] Error loading session:', error);
      setSessionError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [hydrateSession, serializeSession, debouncedSnapshotControl]);

  /**
   * Clear current session state (for starting fresh)
   */
  const clearSession = useCallback(() => {
    
    // Reset session metadata
    setSessionId(null);
    setHasLoadedSession(false);
    setSessionError(null);
    setHasUnsavedChanges(false);
    
    // Reset all hook state
    recordingHook.updateTranscript('');
    clarifyingQuestionsHook.clear();
    diagnosisHook.clearDDX();
    diagnosticPlanHook.clearPlan();
    confirmDiagnosisHook.clearConfirmedDiagnoses();
    treatmentPlanHook.clearPlan();
    dischargeHook.reset();
    chartHook.clearNote();
    
    // Reset local state
    setSelectedDiagnoses([]);
    setCustomDiagnoses([]);
    setClinicianImpression(null);
    setDisabledDiagnosisIds(new Set());
    setDisabledTestIds(new Set());
    setDisabledExamIds(new Set());
    setExamResults(new Map());
    const nextSetting = readDefaultEncounterSetting();
    const nextFormat = readDefaultNoteFormat();
    const nextInpatient = isInpatient(nextSetting);
    const leanMode = nextInpatient ? "table" : "list";
    setExamMode(nextInpatient ? "exam2" : "exam");
    {
      const clearScope = {
        specialty: readDefaultSpecialty(),
        setting: nextSetting,
        inpatient: nextInpatient,
        noteFormat: nextFormat,
      };
      setExam2Systems(
        applyExam2LayoutPrefs(
          createBaseExam2Systems(null),
          loadExam2LayoutPrefs(user?.uid, clearScope),
        ),
      );
    }
    setTestResults(new Map());
    setPlan2ByCategory(createEmptyPlan2ByCategory(leanMode));
    setSelectedConfirmedDiagnoses([]);
    setCustomTreatments([]);
    setDisabledTreatmentIds(new Set());
    setSelectedTreatments(new Set());
    setTreatment2ByCategory(createEmptyTreatment2ByCategory(leanMode));
    setPatientContext(null);
    setEncounterSettingState(nextSetting);
    setNoteFormatState(nextFormat);
    setSpecialtyState(readDefaultSpecialty());
    // New encounters always begin on the setup shell (mic or Continue), not in a stepper.
    setEncounterStarted(false);
    setCurrentStep(STEPS.RECORD);
    setVisitedSteps(new Set([STEPS.RECORD]));
    setInpatientEvents([]);
    setDailyPlanComments({});
    setDailyPlanReassuring({});
    setTextMode(false);
    pearlsRequestRef.current += 1;
    setPearls([]);
    setPearlsSubject("");
    setPearlsError("");
    setPearlsLoading(false);
    
    // Reset refs
    userNavigatedRef.current = false;
    // Keep analysisTriggeredRef true so the auto-analysis effect doesn't immediately
    // re-fire with the stale debouncedTranscript. The transcript-change effect
    // (deps: [recordingHook.transcript]) will flip it back to false once the clear lands.
    analysisTriggeredRef.current = true;
    generatedForRef.current = null;
    lastSavedSnapshotRef.current = null;
  }, [
    recordingHook,
    clarifyingQuestionsHook,
    diagnosisHook,
    diagnosticPlanHook,
    confirmDiagnosisHook,
    treatmentPlanHook,
    dischargeHook,
    chartHook,
    user?.uid,
  ]);

  /**
   * Create a new session (clears current state)
   */
  const createNewSessionHandler = useCallback(async () => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      clearSession(); // Clear all state first
      
      const id = await sessionService.createNewSession(
        user.uid,
        reportingMetadata,
      );
      await sessionService.setActiveSessionId(user.uid, id);
      setSessionId(id);
      
      return id;
    } catch (error) {
      console.error('[useChartMindSession] Error creating session:', error);
      setSessionError(error.message);
      throw error;
    }
  }, [user?.uid, clearSession, reportingMetadata]);

  // Auto-load session on mount if initialSessionId provided
  useEffect(() => {
    async function loadInitialSession() {
      if (!user?.uid) {
        return;
      }
      
      // If initialSessionId provided and we haven't loaded it yet, load that session
      if (initialSessionId && !hasLoadedSession) {
        // Note: isHydratingRef already starts as true (initialized in useState above)
        debouncedSnapshotControl.cancel();
        
        setHasLoadedSession(true); // Set flag immediately to prevent duplicate loads
        try {
          await loadSession(initialSessionId);
        } catch (error) {
          console.error('[useChartMindSession] Failed to auto-load session:', error);
          setHasLoadedSession(false); // Reset on error so they can retry
          isHydratingRef.current = false; // Re-enable saves on error
        }
      }
    }

    loadInitialSession();
  }, [initialSessionId, user?.uid, loadSession, hasLoadedSession, debouncedSnapshotControl]);

  // ============================================================================
  // RETURN GROUPED STATE
  // ============================================================================

  return {
    // Navigation
    navigation: {
      currentStep,
      visitedSteps,
      encounterStarted,
      beginEncounter,
      setCurrentStep: handleStepChange,
      handleContinueToDiagnosis,
      handleContinueToExam,
      handleContinueToPearls,
      handleContinueToPlan,
      handleContinueToConfirm,
      handleContinueToTreatment,
      handleContinueToEvents,
      handleContinueToEventSequence,
      handleContinueToDischarge,
      handleContinueToChart,
      handleSkipToChart,
      handleBack,
    },

    // Inpatient or outpatient, plus what only inpatient encounters collect
    encounter: {
      setting: encounterSetting,
      inpatient,
      eventsNote,
      setSetting: setEncounterSetting,
      noteFormat,
      setNoteFormat,
      specialty,
      setSpecialty,
      events: inpatientEvents,
      addEvent: handleAddInpatientEvent,
      addEventAndContinueToDiagnosis: handleAddEventAndContinueToDiagnosis,
      updateEvent: handleUpdateInpatientEvent,
      removeEvent: handleRemoveInpatientEvent,
      dailyPlanComments,
      dailyPlanReassuring,
      setDailyPlanComment: handleDailyPlanCommentChange,
      setDailyPlanReassuring: handleSetDailyPlanReassuring,
    },

    // Recording
    recording: {
      ...recordingHook,
      debouncedTranscript,
      onMicClick: handleMicClick,
      language: recordingLanguage,
      setLanguage: setRecordingLanguage,
      pasteClarifying,
      setPasteClarifying,
      textMode,
      setTextMode: (next) => {
        // Typing/pasting is the non-mic way to enter the Narrative stepper.
        if (next && !encounterStarted && !eventsNote) beginEncounter(STEPS.RECORD);
        setTextMode(next);
      },
      // True while EITHER device is capturing. `isRecording` above stays honest about this
      // device's own microphone, because it also drives the mic button's stop icon and the
      // transcript field's readOnly.
      captureActive,
      phone: phoneRelay,
      // Dictating one note section from the paired phone. Separate from `phone` because it is a
      // different destination for the same wire, not a different wire.
      phoneDictation: {
        // A phone has actually scanned and is on the other end, so offering this is honest.
        available: Boolean(phoneRelay.phoneClaimedAt),
        target: phoneDictation,
        text: phoneDictationText,
        recording: isPhoneRecording && Boolean(phoneDictation),
        begin: beginPhoneDictation,
        cancel: cancelPhoneDictation,
      },
    },

    // Imported patient CMR (from share code). Every step's generator is fed `payload`,
    // which callLLMAPI renders into the system prompt as the authoritative source for
    // meds / allergies / PMH.
    patientContext: {
      value: patientContext,
      payload: patientContextPayload,
      recordUnavailable: patientRecordUnavailable,
      set: setPatientContext,
      clear: clearPatientContext,
    },

    // Clarifying questions (live-polled while recording)
    clarifyingQuestions: {
      ...clarifyingQuestionsHook,
      enabled: clarifyingQuestionsEnabled,
    },

    // Diagnosis
    //
    // Every retry below is wrapped to inject the patient context. The step components call
    // retry with just the transcript and diagnoses, so without these wrappers a single
    // retry click silently drops the imported record and regenerates without it.
    diagnosis: {
      ...diagnosisHook,
      selectedDiagnoses,
      customDiagnoses,
      clinicianImpression,
      onRecordClinicianImpression: handleRecordClinicianImpression,
      onAddCustomDiagnosis: handleAddCustomDiagnosis,
      disabledDiagnosisIds,
      onSelectionChange: handleSelectionChange,
      onToggleDiagnosisDisabled: handleToggleDiagnosisDisabled,
      onPrefetch: handlePrefetch,
      retry: (transcript) => {
        // Events note type retries from event source text, not the (empty) narrative transcript.
        const source =
          (transcript && String(transcript).trim()) ||
          (eventsNote ? eventsSourceText : recordingHook.transcript);
        diagnosisHook.retry(source, patientContextPayload);
      },
    },

    // Diagnostic Plan
    diagnosticPlan: {
      ...diagnosticPlanHook,
      disabledTestIds,
      disabledExamIds,
      examResults,
      examMode,
      exam2Systems,
      exam2PreferredNormals,
      testResults,
      plan2ByCategory,
      onToggleTestDisabled: handleToggleTestDisabled,
      onToggleExamDisabled: handleToggleExamDisabled,
      onUpdateExamResult: handleUpdateExamResult,
      onSaveObjectiveExamResults: handleSaveObjectiveExamResults,
      onSaveObjectiveExam2: handleSaveObjectiveExam2,
      onExamModeChange: handleExamModeChange,
      onChangeExam2SystemValue: handleChangeExam2SystemValue,
      onMakeExam2DefaultNormal: handleMakeExam2DefaultNormal,
      onCreateNormalExam2: handleCreateNormalExam2,
      onReplaceExam2Systems: handleReplaceExam2Systems,
      onSaveExam2LayoutDefaults: handleSaveExam2LayoutDefaults,
      exam2LayoutScope,
      onUpdateTestResult: handleUpdateTestResult,
      onPlan2ModeChange: handlePlan2ModeChange,
      onPlan2ItemValueChange: handlePlan2ItemValueChange,
      onPlan2ItemFocus: handlePlan2ItemFocus,
      onPlan2ItemBlur: handlePlan2ItemBlur,
      onPlan2DocumentNormal: handlePlan2DocumentNormal,
      keptExamFindings: (diagnosticPlanHook.planData?.examFindings || []).filter(
        (finding) => !disabledExamIds.has(finding.id),
      ),
      retry: (transcript, diagnoses) => {
        diagnosticPlanHook.retry(transcript, diagnoses, patientContextPayload);
      },
    },

    // Confirmed Diagnosis
    confirmDiagnosis: {
      ...confirmDiagnosisHook,
      selectedConfirmedDiagnoses,
      onSelectionChange: handleConfirmedSelectionChange,
      originalDdxData: diagnosisHook.ddxData, // Pass original DDX as seed
      retry: (transcript, testResults, originalDdxData) => {
        confirmDiagnosisHook.retry(
          transcript,
          testResults,
          originalDdxData,
          disabledDiagnosisIds,
          patientContextPayload,
        );
      },
    },

    // Teaching pearls
    pearls: {
      pearls,
      subject: pearlsSubject,
      loading: pearlsLoading,
      error: pearlsError,
      available: !pearlsUnavailable,
      retry: () => {
        setPearlsError("");
        void runPearls(pearlsTarget);
      },
    },

    // Treatment Plan
    treatment: {
      ...treatmentPlanHook,
      customTreatments,
      onAddCustomTreatment: handleAddCustomTreatment,
      disabledTreatmentIds,
      selectedTreatments,
      treatment2ByCategory,
      onToggleTreatmentDisabled: handleToggleTreatmentDisabled,
      onToggleTreatmentSelected: handleToggleTreatmentSelected,
      onTreatment2ModeChange: handleTreatment2ModeChange,
      onTreatment2ItemValueChange: handleTreatment2ItemValueChange,
      onTreatment2ItemFocus: handleTreatment2ItemFocus,
      onTreatment2ItemBlur: handleTreatment2ItemBlur,
      onTreatment2DocumentNormal: handleTreatment2DocumentNormal,
      retry: (transcript, diagnoses, diagnosticPlanData) => {
        treatmentPlanHook.retry(
          transcript,
          diagnoses,
          diagnosticPlanData,
          patientContextPayload,
        );
      },
    },

    // Discharge
    discharge: {
      ...dischargeHook,
    },

    // Chart
    chart: {
      ...chartHook,
      onCopyToClipboard: handleCopyToClipboard,
      onExportToPdf: handleExportToPdf,
      onRegenerate: handleRegenerate,
      isStale: isNoteStale,
      // The key already covers diagnoses, so a note showing with none selected was necessarily
      // generated without them. Drives the "add more detail" prompt on the Chart step.
      generatedWithoutDiagnoses: chartHook.hasNote && selectedDiagnoses.length === 0,
    },

    // Session management
    session: {
      sessionId,
      saving,
      loading,
      error: sessionError,
      hasUnsavedChanges,
      lastSavedAt,
      saveSession,
      loadSession,
      createNewSession: createNewSessionHandler,
      clearSession,
      serializeSession,
      hydrateSession,
      autoSaveSession,
    },
  };
}

export default useChartMindSession;
